import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { API_URI } from "../../helpers";
// Consola flotante del agente de atención automatizado (burbuja arrastrable).
import AgentBubble from "./AgentBubble";

// ────────────────────────────────────────────────────────────────────
//  Channel metadata
// ────────────────────────────────────────────────────────────────────
const CHANNEL = {
  whatsapp: { label: "WhatsApp", color: "#25D366", short: "WA", actions: ["ignore", "close"] },
  meta_fb:  { label: "Facebook", color: "#1877F2", short: "FB", actions: ["hide", "report", "delete", "ignore"] },
  meta_ig:  { label: "Instagram", color: "#E4405F", short: "IG", actions: ["hide", "report", "delete", "ignore"] },
  youtube:  { label: "YouTube", color: "#FF0000", short: "YT", actions: ["hide", "report", "ignore"] },
  email:    { label: "Email", color: "#64748B", short: "@", actions: ["ignore"] },
};

// Canales de "posts" (comentarios públicos): el hilo es el post y junta a muchos.
// En ellos el badge cuenta solo agresivos y al entrar se filtra a agresivos por defecto.
const POST_CHANNELS = ["meta_fb", "meta_ig", "youtube"];
// Mensajes directos (conversación 1:1 que hay que responder), separados de los
// comentarios públicos para un triage más realista.
// Directos "simples" (un canal = una fila). Email va aparte, una fila por buzón.
const DIRECT_CHANNELS = ["whatsapp"];
// Plataformas Meta que además tienen DMs (Messenger / IG Direct): aparecen tanto en
// "Directos" (filtrando kind=dm) como en "Comentarios" (kind=comment).
const DM_CHANNELS = ["meta_fb", "meta_ig"];

// Buzones de email (multi-buzón). La clave matchea inbox_*.mailbox del backend.
const MAILBOX = {
  premium: { label: "Premium", addr: "premium@tusclasesdeguitarra.com", bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  soporte: { label: "Soporte", addr: "soporte@tusclasesdeguitarra.com", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
  mario: { label: "Mario", addr: "mariofreiria@tusclasesdeguitarra.com", bg: "#EDE9FE", fg: "#5B21B6", dot: "#8B5CF6" },
};

const ACTION_META = {
  ignore: { label: "Ignorar", icon: "archive", danger: false, confirm: false },
  close:  { label: "Cerrar conversación", icon: "check", danger: false, confirm: true },
  hide:   { label: "Ocultar", icon: "eye_off", danger: false, confirm: false },
  // Ojo: "report" denuncia el comentario ANTE LA PLATAFORMA (Meta/YouTube). No es lo
  // mismo que nuestra carpeta Spam, que sólo afecta a este panel.
  report: { label: "Denunciar en la plataforma", icon: "flag", danger: true, confirm: false },
  delete: { label: "Eliminar definitivamente", icon: "trash", danger: true, confirm: true },
};

const SENTIMENT = {
  negative: { label: "Negativo", bg: "#FEE2E2", fg: "#B91C1C", dot: "#DC2626" },
  neutral:  { label: "Neutral",  bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8" },
  positive: { label: "Positivo", bg: "#D1FAE5", fg: "#065F46", dot: "#10B981" },
};

// Semáforo de hostilidad hacia Mario / TCDG.
// score viene 0–1 desde la IA; target ∈ {mario, tcdg, both, other, none}.
const HOSTILITY = {
  none:   { label: "Sin agresión",       bg: "#ECFDF5", fg: "#047857", dot: "#10B981" },
  low:    { label: "Crítica leve",       bg: "#FEF9C3", fg: "#854D0E", dot: "#F59E0B" },
  medium: { label: "Hostil",             bg: "#FFEDD5", fg: "#9A3412", dot: "#F97316" },
  high:   { label: "Muy agresivo",       bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
};
const hostilityLevel = (score, target) => {
  if (score == null) return null;
  // Si no apunta a Mario/TCDG, lo bajamos a "none" salvo agresión muy alta.
  const aimed = target === "mario" || target === "tcdg" || target === "premium" || target === "both";
  if (!aimed) return score >= 0.85 ? "medium" : "none";
  if (score >= 0.7) return "high";
  if (score >= 0.45) return "medium";
  if (score >= 0.2) return "low";
  return "none";
};
const HOSTILITY_TARGET = {
  mario:   "Mario",
  tcdg:    "TCDG",
  premium: "Premium",
  both:    "Mario y TCDG",
  other:   "terceros",
  none:    "",
};

const STATUS_PILL = {
  pending:  { label: "Pendiente",   bg: "#FEF3C7", fg: "#92400E" },
  replied:  { label: "Respondido",  bg: "#DBEAFE", fg: "#1E40AF" },
  hidden:   { label: "Oculto",      bg: "#E5E7EB", fg: "#374151" },
  deleted:  { label: "Eliminado",   bg: "#FEE2E2", fg: "#991B1B" },
  reported: { label: "Reportado",   bg: "#FCE7F3", fg: "#9D174D" },
  ignored:  { label: "Ignorado",    bg: "#F3F4F6", fg: "#6B7280" },
  spam:     { label: "Spam",        bg: "#FEE2E2", fg: "#B91C1C" },
};

// ────────────────────────────────────────────────────────────────────
//  Inline SVG icons (24px stroke). Lucide-style.
// ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor", strokeWidth = 2 }) => {
  const paths = {
    inbox: "M22 12h-6l-2 3h-4l-2-3H2 M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z",
    sparkles: "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z M20 3v4 M22 5h-4 M4 17v2 M5 18H3",
    refresh: "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8 M21 3v5h-5 M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16 M8 16H3v5",
    search: "m21 21-4.34-4.34 M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z",
    send: "m22 2-7 20-4-9-9-4 20-7Z M22 2 11 13",
    alert: "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z M12 9v4 M12 17h.01",
    star: "M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
    eye_off: "m9.88 9.88 a3 3 0 1 0 4.24 4.24 M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68 M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61 M2 2l20 20",
    trash: "M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6",
    flag: "M4 22V4 M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1",
    archive: "m21 8-4.5 4.5L12 8 M3 8h18 M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8 M10 12h4",
    more: "M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
    chevron_down: "m6 9 6 6 6-6",
    external: "M15 3h6v6 M10 14 21 3 M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
    check: "M20 6 9 17l-5-5",
    chevron_right: "m9 18 6-6-6-6",
    x: "M18 6 6 18 M6 6l12 12",
    filter: "M22 3H2l8 9.46V19l4 2v-8.54L22 3z",
    info: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z M12 16v-4 M12 8h.01",
    book: "M4 19.5A2.5 2.5 0 0 1 6.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z",
    plus: "M5 12h14 M12 5v14",
    edit: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
    globe: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z",
    paperclip: "m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48",
    file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6",
    download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3",
    credit_card: "M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z M1 10h22",
    shield: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z M9.5 9.5l5 5 M14.5 9.5l-5 5",
    shield_off: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z M9 12l2 2 4-4",
    settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z",
    undo: "M3 7v6h6 M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13",
    // Trazados al estilo del resto del set (sólo stroke, sin relleno).
    menu: "M3 6h18 M3 12h18 M3 18h18",
    back: "M19 12H5 M12 19l-7-7 7-7",
    play: "m6 3 14 9-14 9Z",
    pause: "M10 4v16 M15 4v16",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name]} />
    </svg>
  );
};

// Spinner inline (SVG con SMIL — anda en Safari sin keyframes CSS). Para acciones lentas.
const Spinner = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" style={{ verticalAlign: "middle" }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" opacity="0.9">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.7s" repeatCount="indefinite" />
    </path>
  </svg>
);

// Barra "energía" de sentimiento: muestra intensidad (0-1) coloreada.
const SentimentBar = ({ sentiment, score }) => {
  const meta = SENTIMENT[sentiment];
  if (!meta) return null;
  const pct = Math.max(0, Math.min(1, score == null ? 0.5 : score)) * 100;
  return (
    <span
      title={`${meta.label} · ${Math.round(pct)}%`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: meta.bg, color: meta.fg, borderRadius: 999,
        padding: "3px 8px 3px 6px", fontSize: 11, fontWeight: 600, lineHeight: 1,
      }}>
      <span style={{
        width: 28, height: 5, background: "rgba(0,0,0,0.08)",
        borderRadius: 999, overflow: "hidden", display: "inline-block",
      }}>
        <span style={{
          display: "block", height: "100%", width: `${pct}%`,
          background: meta.dot, transition: "width .2s",
        }} />
      </span>
      {meta.label}
    </span>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Styles
// ────────────────────────────────────────────────────────────────────
// Un solo breakpoint. El layout de escritorio son TRES paneles fijos (bandeja + lista +
// conversación) y abajo de ~900px no entran: ahí la bandeja pasa a ser un cajón y la lista
// y la conversación se turnan, como cualquier app de mensajes en el celular.
const MOBILE = "@media (max-width: 900px)";

/** ¿Estamos en el layout de celular? Mismo corte que la media query, para no desincronizar. */
const esCelular = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(max-width: 900px)").matches
    : false;

/**
 * Lo que dice la barra cuando el panel está plegado. Tiene que alcanzar para NO abrirlo:
 * si es alumno, hasta cuándo tiene; si hay plata esperando, cuántos pagos; y si no se
 * encontró nada, decirlo. Plegar algo sin dejar su titular a la vista es esconderlo.
 */
const resumenActivacion = (acc, actionable, a) => {
  const partes = [];
  if (acc && acc.exists) {
    partes.push(acc.active ? "Alumno vigente" : "Alumno vencido");
    if (acc.current_end) partes.push("vence " + acc.current_end);
  } else if (a) {
    partes.push("Sin cuenta");
  }
  const n = (actionable || []).length;
  if (n) partes.push(n + (acc && acc.exists ? " para renovar" : " sin vincular"));
  else if (a && Array.isArray(a.receipts) && a.receipts.length) partes.push("comprobante adjunto");
  else if (a && a.activation_request) partes.push("pago no encontrado");
  return partes.length ? partes.join(" · ") : "Cuenta y pagos";
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    // 100dvh y no 100vh: en el celular la barra del navegador se suma al 100vh y el
    // panel queda más alto que la pantalla, así que la barra de responder se va abajo
    // del pliegue. `dvh` descuenta el chrome del navegador. El 100vh queda de fallback
    // para los que no lo soportan.
    height: "100vh",
    "@supports (height: 100dvh)": { height: "100dvh" },
    width: "100vw",
    background: "#F8FAFC",
    overflow: "hidden",
    fontFamily: "-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',Roboto,sans-serif",
    color: "#0F172A",
    fontSize: 13,
  },

  // ── Top bar ──────────────────────────────────────────────
  topbar: {
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    height: 56,
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    flexShrink: 0,
    "& > * + *": { marginLeft: 16 },
    [MOBILE]: { padding: "0 10px", "& > * + *": { marginLeft: 8 } },
  },

  // El texto de los botones del topbar: primero en caer cuando no hay ancho.
  // Con el icono alcanza para reconocerlos y el title sigue explicando cada uno.
  topActionLabel: { [MOBILE]: { display: "none" } },
  burger: {
    display: "none",
    width: 34, height: 34, borderRadius: 8, flexShrink: 0,
    border: "1px solid #E2E8F0", background: "#fff", color: "#475569",
    alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
    [MOBILE]: { display: "flex" },
  },
  // Velo detrás de la bandeja abierta.
  navScrim: {
    display: "none",
    position: "fixed", inset: 0, zIndex: 1200,
    background: "rgba(15,23,42,0.45)",
    [MOBILE]: { display: "block" },
  },
  brand: {
    display: "flex", alignItems: "center",
    fontSize: 15, fontWeight: 700, color: "#0F172A", letterSpacing: -0.2,
    // El nombre completo no entra: en celular queda sólo el isotipo.
    [MOBILE]: { fontSize: 0, "& > div": { marginRight: 0 } },
  },
  brandIcon: {
    width: 32, height: 32, borderRadius: 8,
    background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", boxShadow: "0 2px 6px rgba(79,70,229,0.3)",
    marginRight: 12, flexShrink: 0,
  },
  topSearch: {
    flex: 1, maxWidth: 480, position: "relative",
    display: "flex", alignItems: "center",
    [MOBILE]: { maxWidth: "none" },
  },
  topSearchInput: {
    width: "100%", padding: "8px 12px 8px 34px",
    border: "1px solid #E2E8F0", borderRadius: 8,
    fontSize: 13, outline: "none", boxSizing: "border-box",
    background: "#F8FAFC", transition: "all 0.15s",
    fontFamily: "inherit",
    "&:focus": { borderColor: "#6366F1", background: "#fff", boxShadow: "0 0 0 3px rgba(99,102,241,0.1)" },
  },
  topSearchIcon: {
    position: "absolute", left: 11, color: "#94A3B8",
    display: "flex", pointerEvents: "none",
  },
  topAction: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "7px 14px",
    border: "1px solid #E2E8F0", borderRadius: 8,
    background: "#fff", color: "#334155",
    fontSize: 12, fontWeight: 600, cursor: "pointer",
    transition: "all 0.15s",
    "&:hover": { borderColor: "#CBD5E1", background: "#F8FAFC" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    "& > svg, & > span > svg": { marginRight: 4, flexShrink: 0 },
    // Sin texto queda un botón cuadrado de icono; no debe encogerse ni dejar hueco.
    [MOBILE]: {
      padding: 8, flexShrink: 0, gap: 0,
      "& > svg, & > span > svg": { marginRight: 0 },
    },
  },
  syncMeta: {
    fontSize: 11, color: "#94A3B8",
    minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    maxWidth: 280,
    // Dato de contexto: lo primero que se sacrifica cuando no hay ancho.
    [MOBILE]: { display: "none" },
  },

  // ── Body ─────────────────────────────────────────────────
  body: { display: "flex", flex: 1, minHeight: 0, position: "relative" },

  // ── Sidebar (dark warm — paleta TCDG) ────────────────────
  sidebar: {
    width: 180, flexShrink: 0,
    background: "#161412", borderRight: "1px solid #4a443e",
    padding: "16px 8px", overflowY: "auto",
    // En celular sale del flujo y entra como cajón desde la izquierda.
    [MOBILE]: {
      position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 1250,
      width: 232, paddingTop: 14,
      transform: "translateX(-100%)",
      transition: "transform .22s ease-out",
      boxShadow: "0 0 40px rgba(0,0,0,0.5)",
    },
  },
  sidebarOpen: { [MOBILE]: { transform: "none" } },
  sectionLabel: {
    fontSize: 10, fontWeight: 700, color: "#8d8678",
    letterSpacing: 0.8, textTransform: "uppercase",
    margin: "16px 8px 6px", display: "flex", alignItems: "center",
    "&:first-child": { marginTop: 4 },
    "& > svg": { marginRight: 8, flexShrink: 0 },
  },
  navBtn: {
    display: "flex", alignItems: "center",
    width: "100%", padding: "8px 10px", marginBottom: 1,
    border: 0, borderRadius: 8, background: "transparent",
    cursor: "pointer", fontSize: 13, color: "#c9c3b8",
    textAlign: "left", fontFamily: "inherit", fontWeight: 500,
    transition: "background 0.1s, color 0.1s",
    "&:hover": { background: "#2c2925", color: "#f7f5f0" },
    "& > svg": { marginRight: 10, flexShrink: 0 },
  },
  navBtnActive: {
    background: "#34302a !important", color: "#e9b163 !important", fontWeight: 700,
    boxShadow: "inset 3px 0 0 #e9b163",
  },
  navBtnLabel: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  navBadge: {
    fontSize: 10, fontWeight: 700,
    background: "#2c2925", color: "#8d8678",
    padding: "2px 6px", borderRadius: 10, minWidth: 18, textAlign: "center",
    marginLeft: "auto", flexShrink: 0,
  },
  navBadgeAlert: { background: "#5a2a26", color: "#f7b3a0" },
  navBadgeSales: { background: "#2d4a3a", color: "#a8e8c4" },
  navBadgeActive: { background: "#e9b163", color: "#1f1d1a" },
  channelChip: {
    width: 18, height: 18, borderRadius: 4,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontSize: 9, fontWeight: 700, flexShrink: 0,
    marginRight: 10,
  },
  navRow: {
    position: "relative", display: "flex", alignItems: "center",
    "&:hover $ignoreAllBtn": { opacity: 1 },
  },
  ignoreAllBtn: {
    flexShrink: 0, border: 0, background: "transparent", cursor: "pointer",
    color: "#8d8678", padding: "4px 7px", marginLeft: 2, borderRadius: 6,
    opacity: 0, transition: "opacity .1s, background .1s, color .1s",
    display: "inline-flex", alignItems: "center",
    "&:hover": { background: "#3a2320", color: "#f7b3a0" },
  },
  aggToggle: {
    display: "flex", gap: 4, margin: "2px 8px 4px", padding: 3,
    background: "#26231f", borderRadius: 8,
  },
  aggToggleBtn: {
    flex: 1, border: 0, background: "transparent", cursor: "pointer",
    color: "#c9c3b8", fontSize: 11, fontWeight: 600, padding: "5px 6px",
    borderRadius: 6, fontFamily: "inherit",
    "&:hover": { color: "#f7f5f0" },
  },
  aggToggleBtnActive: { background: "#34302a", color: "#e9b163", fontWeight: 700 },

  // ── List column ──────────────────────────────────────────
  list: {
    width: 380, flexShrink: 0,
    background: "#fff", borderRight: "1px solid #E2E8F0",
    display: "flex", flexDirection: "column", minHeight: 0,
    // Ocupa toda la pantalla; al abrir una conversación se esconde (ver bodyPicked).
    [MOBILE]: { width: "100%", borderRight: "none" },
  },
  listHead: {
    padding: "14px 16px 10px", borderBottom: "1px solid #F1F5F9",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  listHeadTitle: { fontSize: 13, fontWeight: 700, color: "#0F172A" },
  listHeadCount: { fontSize: 11, color: "#94A3B8", fontWeight: 500 },
  listScroll: { flex: 1, overflowY: "auto" },
  msgItem: {
    padding: "12px 16px", borderBottom: "1px solid #F1F5F9",
    cursor: "pointer", display: "flex",
    transition: "background 0.1s",
    "&:hover": { background: "#F8FAFC" },
  },
  msgItemActive: {
    background: "#EEF2FF !important",
    boxShadow: "inset 3px 0 0 #4F46E5",
  },
  avatar: {
    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontWeight: 700, fontSize: 13,
    position: "relative", marginRight: 12,
  },
  avatarChannel: {
    position: "absolute", bottom: -2, right: -2,
    width: 14, height: 14, borderRadius: "50%",
    border: "2px solid #fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontSize: 7, fontWeight: 800,
  },
  msgBody: { flex: 1, minWidth: 0 },
  msgRow1: {
    display: "flex", alignItems: "baseline", marginBottom: 2,
    "& > * + *": { marginLeft: 8 },
  },
  msgAuthor: {
    fontWeight: 600, fontSize: 13, color: "#0F172A",
    flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  msgTime: { fontSize: 11, color: "#94A3B8", flexShrink: 0, fontWeight: 500 },
  msgPreview: {
    fontSize: 12, color: "#64748B", lineHeight: 1.45,
    overflow: "hidden", textOverflow: "ellipsis",
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
    marginBottom: 6, wordBreak: "break-word",
  },
  msgTags: { display: "flex", flexWrap: "wrap", "& > *": { marginRight: 6, marginTop: 2 } },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 5,
    fontSize: 10, padding: "2px 8px", borderRadius: 4,
    fontWeight: 600, lineHeight: 1.4,
  },

  // ── Detail column ────────────────────────────────────────
  detail: {
    flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
    background: "#FAFBFC",
    [MOBILE]: { display: "none" },
  },
  // Con una conversación abierta, en celular se turnan: se esconde la lista y se
  // muestra la conversación a pantalla completa.
  bodyPicked: {
    [MOBILE]: {
      "& $list": { display: "none" },
      "& $detail": { display: "flex" },
      "& $empty": { display: "none" },
    },
  },
  // Volver a la lista. Sólo en celular: en escritorio los dos paneles conviven.
  backBtn: {
    display: "none",
    width: 32, height: 32, borderRadius: 8, flexShrink: 0, marginRight: 4,
    border: "1px solid #E2E8F0", background: "#fff", color: "#475569",
    alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0,
    [MOBILE]: { display: "flex" },
  },
  detailHead: {
    padding: "14px 24px",
    [MOBILE]: { padding: "10px 12px" },
    background: "#fff",
    borderBottom: "1px solid #E2E8F0",
    display: "flex", alignItems: "center", flexShrink: 0,
    "& > * + *": { marginLeft: 12 },
  },
  detailAvatarBig: {
    width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontWeight: 700, fontSize: 14,
  },
  detailMain: { flex: 1, minWidth: 0 },
  detailAuthor: { fontWeight: 600, fontSize: 15, color: "#0F172A", marginBottom: 2 },
  detailMeta: {
    fontSize: 12, color: "#64748B",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  detailHandle: { color: "#94A3B8" },

  // ── Panel de cuenta / pagos ──────────────────────────────
  // En escritorio va desplegado, como siempre. En celular se pliega, porque con él abierto
  // el hilo de mensajes quedaba en 40px de alto y la pantalla mostraba todo menos la
  // conversación. Plegado deja a la vista el titular (vigencia, pagos) para no esconderlo.
  actPanel: {
    background: "#EEF2FF", borderBottom: "2px solid #C7D2FE",
    padding: "7px 16px", display: "flex", flexDirection: "column", gap: 6, flexShrink: 0,
    [MOBILE]: { padding: "4px 10px", gap: 4, maxHeight: "42vh", overflowY: "auto" },
  },
  actPanelCerrado: {
    [MOBILE]: { maxHeight: "none", overflow: "visible", "& $actPanelCuerpo": { display: "none" } },
  },
  actPanelCuerpo: { display: "flex", flexDirection: "column", gap: 6 },
  actPanelToggle: {
    display: "none",
    [MOBILE]: {
      display: "flex", alignItems: "center", gap: 8, width: "100%",
      background: "none", border: 0, padding: "5px 2px", cursor: "pointer",
      font: "inherit", textAlign: "left", color: "#3730A3",
    },
  },
  actPanelResumen: {
    flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  actPanelChevron: { fontSize: 12, fontWeight: 700, flexShrink: 0, color: "#6366F1" },

  payRow: {
    display: "flex", alignItems: "center", gap: 8,
    borderRadius: 8, padding: "5px 10px",
    [MOBILE]: { flexWrap: "wrap", rowGap: 6 },
  },

  // ── Thread ───────────────────────────────────────────────
  threadArea: {
    flex: 1, overflowY: "auto", padding: "20px 24px",
    // Sin un mínimo, un panel de arriba que no cede espacio lo aplasta hasta dejarlo en
    // dos dedos de alto. El mínimo obliga a que el hilo exista aunque algo más se pase.
    [MOBILE]: { padding: "12px 12px", minHeight: 96 },
  },
  daySep: {
    display: "flex", alignItems: "center", gap: 12,
    margin: "12px 0 16px", color: "#94A3B8", fontSize: 11, fontWeight: 600,
    "&::before, &::after": {
      content: '""', flex: 1, height: 1, background: "#E2E8F0",
    },
  },
  bubbleWrap: {
    display: "flex", flexDirection: "column", marginBottom: 10,
  },
  bubbleWrapOut: { alignItems: "flex-end" },
  bubbleAuthor: {
    fontSize: 12.5, color: "#475569", fontWeight: 700,
    margin: "0 12px 4px",
  },
  bubble: {
    maxWidth: "70%", padding: "10px 14px", borderRadius: 12,
    fontSize: 14.5, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  bubbleIn: { background: "#fff", border: "1px solid #E2E8F0", color: "#0F172A", borderTopLeftRadius: 4 },
  bubbleOut: {
    background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
    color: "#fff", borderTopRightRadius: 4,
  },
  bubbleTime: {
    fontSize: 10, marginTop: 4, opacity: 0.8, fontWeight: 500,
  },

  // ── AI suggestion card ───────────────────────────────────
  aiCard: {
    margin: "0 24px 12px",
    padding: 14,
    background: "linear-gradient(135deg, #F5F3FF 0%, #FEF3F2 100%)",
    border: "1px solid #DDD6FE",
    borderRadius: 10,
    fontSize: 12, color: "#3730A3",
  },
  aiCardHead: {
    display: "flex", alignItems: "center", gap: 6,
    fontSize: 11, fontWeight: 700, color: "#5B21B6",
    textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6,
  },
  aiCardBody: { color: "#475569", lineHeight: 1.5 },

  // ── Composer ─────────────────────────────────────────────
  composer: {
    background: "#fff",
    borderTop: "1px solid #E2E8F0",
    padding: 16, flexShrink: 0,
    // Columna que PUEDE achicarse: lo que cede es el editor, nunca la botonera. Con el
    // composer rígido, en una pantalla de 667px el botón Enviar terminaba en y=674 —
    // literalmente abajo del borde— y no había forma de mandar la respuesta desde el
    // teléfono. Lo que garantiza que Enviar siempre se vea es esto, no un alto afinado a
    // mano: los altos se rompen en cuanto aparece una pantalla más chica.
    display: "flex", flexDirection: "column", minHeight: 0,
    // La franja de abajo es el lugar RESERVADO para la burbuja del agente, que flota fija
    // en esa esquina. Sin ella, la botonera —que scrollea en horizontal— dejaba «Mejorar»
    // y «Plantillas» debajo de la burbuja, o sea inalcanzables desde el celular.
    // flexShrink en celular: sin esto la columna no cede y el desborde sale por abajo,
    // que es exactamente donde vive el botón de Enviar.
    [MOBILE]: { padding: "10px 10px 88px", flexShrink: 1 }, // 88 = burbuja (74) + margen (10) + aire
  },
  composerInput: {
    width: "100%", minHeight: 80, padding: 12,
    border: "1px solid #E2E8F0", borderRadius: 8,
    fontSize: 15, fontFamily: "inherit", lineHeight: 1.6, color: "#1F2937",
    resize: "vertical", outline: "none", boxSizing: "border-box",
    background: "#F8FAFC", transition: "all 0.15s",
    "&:focus": { borderColor: "#6366F1", background: "#fff", boxShadow: "0 0 0 3px rgba(99,102,241,0.1)" },
  },
  composerEditor: {
    width: "100%", minHeight: 80, padding: 12,
    border: "1px solid #E2E8F0", borderRadius: 8,
    fontSize: 15, fontFamily: "inherit", lineHeight: 1.6, color: "#1F2937",
    outline: "none", boxSizing: "border-box", overflow: "auto",
    background: "#F8FAFC", transition: "border-color 0.15s, background 0.15s", whiteSpace: "pre-wrap", wordBreak: "break-word",
    // Sin techo, un borrador largo empuja el hilo fuera de la pantalla: se lee el borrador
    // y no el mensaje al que contesta. Con techo, el borrador scrollea solo.
    [MOBILE]: { flex: "1 1 auto", maxHeight: "30vh", minHeight: 48, fontSize: 14.5 },
    "&:focus": { borderColor: "#6366F1", background: "#fff", boxShadow: "0 0 0 3px rgba(99,102,241,0.1)" },
    "&:empty:before": { content: "attr(data-placeholder)", color: "#94A3B8", pointerEvents: "none" },
  },
  composerActions: {
    // Una sola línea: nada de wrap. El separador (flex:1) colapsa si falta lugar; si aún
    // así no entran (pantalla muy angosta), scroll horizontal en vez de pasar a 2 líneas.
    display: "flex", marginTop: 12, alignItems: "center",
    flexWrap: "nowrap", gap: 6, overflowX: "auto",
    [MOBILE]: { marginTop: 10, flexShrink: 0 },
  },
  btn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 11px", border: 0, borderRadius: 8,
    fontSize: 12, fontWeight: 600, cursor: "pointer",
    whiteSpace: "nowrap", flexShrink: 0,
    fontFamily: "inherit", transition: "all 0.15s",
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    "& > svg": { flexShrink: 0 },
    "& > svg:first-child": { marginRight: 4 },
    "& > svg:last-child": { marginLeft: 4 },
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
    color: "#fff",
    boxShadow: "0 1px 3px rgba(79,70,229,0.3)",
    "&:hover:not(:disabled)": { transform: "translateY(-1px)", boxShadow: "0 4px 8px rgba(79,70,229,0.35)" },
  },
  btnGhost: {
    background: "transparent", color: "#475569",
    border: "1px solid #E2E8F0",
    "&:hover:not(:disabled)": { background: "#F1F5F9", borderColor: "#CBD5E1" },
  },
  btnDanger: {
    background: "transparent", color: "#B91C1C",
    border: "1px solid #FECACA",
    "&:hover:not(:disabled)": { background: "#FEF2F2" },
  },
  spacer: { flex: 1 },

  // ── Close + more menu ────────────────────────────────────
  iconBtn: {
    width: 32, height: 32, display: "inline-flex",
    alignItems: "center", justifyContent: "center",
    border: "1px solid transparent", borderRadius: 8,
    background: "transparent", color: "#64748B",
    cursor: "pointer", padding: 0, flexShrink: 0,
    transition: "all 0.15s",
    "&:hover": { background: "#F1F5F9", color: "#0F172A", borderColor: "#E2E8F0" },
  },
  moreWrap: { position: "relative", display: "inline-flex" },
  morePop: {
    position: "absolute", bottom: "calc(100% + 6px)", right: 0,
    background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
    boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
    padding: 6, minWidth: 200, zIndex: 20,
  },
  morePopItem: {
    display: "flex", alignItems: "center", gap: 10,
    width: "100%", padding: "8px 12px", borderRadius: 6,
    border: 0, background: "transparent", cursor: "pointer",
    fontSize: 12, fontFamily: "inherit", color: "#334155",
    textAlign: "left", fontWeight: 500,
    "&:hover": { background: "#F1F5F9" },
    "& > svg": { marginRight: 6, flexShrink: 0 },
  },
  morePopItemDanger: { color: "#B91C1C", "&:hover": { background: "#FEF2F2" } },

  // ── Empty state ──────────────────────────────────────────
  empty: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
    flexDirection: "column", color: "#94A3B8",
    padding: 40, textAlign: "center",
    "& > * + *": { marginTop: 12 },

    // Sin conversación elegida, en celular manda la lista: el cartel de "elegí una"
    // ocuparía la pantalla entera y taparía justamente la lista de donde hay que elegir.
    [MOBILE]: { display: "none" },
  },
  emptyIcon: {
    width: 64, height: 64, borderRadius: 16,
    background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center",
    color: "#CBD5E1",
  },
  emptyTitle: { fontSize: 14, fontWeight: 600, color: "#475569" },
  emptyText: { fontSize: 12, maxWidth: 280, lineHeight: 1.5 },

  // ── Animations ───────────────────────────────────────────
  "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
  spinning: { animation: "$spin 0.8s linear infinite", display: "inline-flex" },
  "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(2px)" }, to: { opacity: 1, transform: "translateY(0)" } },
  fadeIn: { animation: "$fadeIn 0.2s ease-out" },
});

// ────────────────────────────────────────────────────────────────────
//  Helpers
// ────────────────────────────────────────────────────────────────────
function fmtAgo(d) {
  if (!d) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 30) return "ahora";
  if (sec < 60) return `hace ${sec}s`;
  if (sec < 3600) return `hace ${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)}h`;
  return `hace ${Math.floor(sec / 86400)}d`;
}
function fmtTime(s) {
  if (!s) return "";
  const d = new Date(s);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  const diff = now - d;
  if (diff < 86400000 * 7) return d.toLocaleDateString("es-AR", { weekday: "short" });
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
}
function fmtBubbleTime(s) {
  if (!s) return "";
  return new Date(s).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
}
function fmtDayLabel(s) {
  const d = new Date(s);
  const today = new Date(); today.setHours(0,0,0,0);
  const yest  = new Date(today); yest.setDate(yest.getDate()-1);
  const dStart = new Date(d); dStart.setHours(0,0,0,0);
  if (dStart.getTime() === today.getTime()) return "Hoy";
  if (dStart.getTime() === yest.getTime())  return "Ayer";
  return d.toLocaleDateString("es-AR", { weekday: "long", day: "2-digit", month: "long" });
}
function avatarColor(seed) {
  if (!seed) return "#94A3B8";
  const colors = ["#6366F1","#10B981","#F59E0B","#EC4899","#06B6D4","#8B5CF6","#EF4444","#14B8A6"];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return colors[h % colors.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]).join("").toUpperCase();
}
function postJson(url, body) {
  const fd = new FormData();
  Object.keys(body || {}).forEach((k) => {
    if (body[k] !== undefined && body[k] !== null) fd.append(k, body[k]);
  });
  return fetch(API_URI + url, { method: "POST", body: fd }).then((r) => r.json());
}

// ────────────────────────────────────────────────────────────────────
//  Render de mensajes (markdown-lite + linkify). Seguro: arma nodos React,
//  no usa dangerouslySetInnerHTML. Resuelve emails que llegan en Markdown
//  (avisos de contacto: ## headers, [txt](url)) y cadenas citadas largas.
// ────────────────────────────────────────────────────────────────────
const safeHref = (u) => (/^https?:\/\//i.test(u) ? u : null);

// Inline: [txt](url), **bold**, *italic*, URLs sueltas y emails.
function inlineNodes(text, kp, isOut) {
  const linkColor = isOut ? "#DCE3FF" : "#4F46E5";
  const RE = /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
  const out = [];
  let last = 0, i = 0, m;
  while ((m = RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = kp + "-" + i;
    if (m[1]) {
      const href = safeHref(m[3]);
      out.push(href ? <a key={k} href={href} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: "underline" }}>{m[2]}</a> : m[1]);
    } else if (m[4]) {
      out.push(<strong key={k}>{m[5]}</strong>);
    } else if (m[6]) {
      out.push(<em key={k}>{m[7]}</em>);
    } else if (m[8]) {
      out.push(<a key={k} href={m[8]} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: "underline", wordBreak: "break-all" }}>{m[8]}</a>);
    } else if (m[9]) {
      out.push(<a key={k} href={"mailto:" + m[9]} style={{ color: linkColor, textDecoration: "underline" }}>{m[9]}</a>);
    }
    last = RE.lastIndex; i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function parseBlocks(text) {
  const lines = text.split("\n");
  const blocks = [];
  let para = [], list = null, quote = null;
  const flushPara = () => { if (para.length) { blocks.push({ t: "p", lines: para }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ t: "ul", items: list }); list = null; } };
  const flushQuote = () => { if (quote) { blocks.push({ t: "quote", lines: quote }); quote = null; } };
  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, "");
    if (line.trim() === "") { flushPara(); flushList(); flushQuote(); return; }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    const q = line.match(/^>\s?(.*)$/);
    const li = line.match(/^\s*[-*]\s+(.*)$/);
    if (h) { flushPara(); flushList(); flushQuote(); blocks.push({ t: "h", lvl: h[1].length, text: h[2] }); return; }
    if (q) { flushPara(); flushList(); if (!quote) quote = []; quote.push(q[1]); return; }
    if (li) { flushPara(); flushQuote(); if (!list) list = []; list.push(li[1]); return; }
    flushList(); flushQuote(); para.push(line);
  });
  flushPara(); flushList(); flushQuote();
  return blocks;
}

function renderBlocks(blocks, isOut, kp) {
  return blocks.map((b, idx) => {
    const key = kp + idx;
    if (b.t === "field") {
      return (
        <div key={key} style={{ margin: idx ? "7px 0 0" : 0 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: isOut ? "rgba(255,255,255,0.72)" : "#94A3B8", marginBottom: 1 }}>{b.label}</div>
          {b.lines.length > 0 && (
            <div style={{ fontWeight: 500 }}>{b.lines.map((l, j) => <React.Fragment key={key + "-" + j}>{j ? <br /> : null}{inlineNodes(l, key + j, isOut)}</React.Fragment>)}</div>
          )}
        </div>
      );
    }
    if (b.t === "h") {
      const size = b.lvl <= 1 ? 15 : b.lvl === 2 ? 14 : 13;
      return <div key={key} style={{ fontWeight: 700, fontSize: size, margin: idx ? "8px 0 1px" : "0 0 1px", lineHeight: 1.3 }}>{inlineNodes(b.text, key, isOut)}</div>;
    }
    if (b.t === "ul") {
      return <ul key={key} style={{ margin: "4px 0", paddingLeft: 18 }}>{b.items.map((it, j) => <li key={key + "-" + j} style={{ margin: "1px 0" }}>{inlineNodes(it, key + j, isOut)}</li>)}</ul>;
    }
    if (b.t === "quote") {
      return <div key={key} style={{ borderLeft: `3px solid ${isOut ? "rgba(255,255,255,0.45)" : "#CBD5E1"}`, paddingLeft: 10, margin: "4px 0", opacity: 0.85 }}>{b.lines.map((l, j) => <div key={key + "-" + j}>{inlineNodes(l, key + j, isOut)}</div>)}</div>;
    }
    return <p key={key} style={{ margin: idx ? "6px 0 0" : 0 }}>{b.lines.map((l, j) => <React.Fragment key={key + "-" + j}>{j ? <br /> : null}{inlineNodes(l, key + j, isOut)}</React.Fragment>)}</p>;
  });
}

// opts.full = true: modo "ver conversación completa" → NO recorta la cadena (no saca pie/
// preámbulos que truncan hasta el final). Solo normaliza espacios. El preview colapsado sí
// limpia el pie para verse prolijo.
function cleanEmailText(text, opts = {}) {
  let t = (text || "").replace(/\r\n/g, "\n");
  // left-trim por línea (las versiones texto traen tabs/espacios de indentación basura)
  t = t.split("\n").map((l) => l.replace(/^[ \t]+/, "")).join("\n");
  if (!opts.full) {
    // preámbulo de reenvío: cortar todo hasta "Mensaje Original / Original Message"
    const ls = t.split("\n");
    const sepIdx = ls.findIndex((l) => /^-*\s*(mensaje original|original message|mensaje reenviado|forwarded message)\s*-*$/i.test(l.trim()));
    if (sepIdx > -1 && sepIdx <= 14) t = ls.slice(sepIdx + 1).join("\n");
    // logo markdown [tcdg](url) al inicio
    t = t.replace(/^\s*\[[^\]]*\]\(https?:\/\/[^\s)]+\)\s*\n+/, "");
    // footer típico: tagline + copyright + sección "Links: ----" de footnotes
    t = t.replace(/\n+\s*Tu plataforma de enseñanza[\s\S]*$/i, "");
    t = t.replace(/\n+\s*Links:\s*\n-+[\s\S]*$/i, "");
    t = t.replace(/\n+\s*COPYRIGHT \(c\)[^\n]*/gi, "");
    t = t.replace(/\n+\s*©[^\n]*$/g, "");
    t = t.replace(/\n+[^\n]*All rights reserved[^\n]*$/gi, "");
    // marcadores de footnote sueltos: " [1]", "[2] [3] [4]"
    t = t.replace(/(^|[ \t])\[\d+\](?=[ \t]|$)/gm, "$1");
  }
  // colapsar espacios/saltos
  t = t.replace(/[ \t]{2,}/g, " ");
  t = t.replace(/\n{3,}/g, "\n\n");
  return t.trim();
}

// "Campo:" como label de ficha (ASUNTO:, Nombre:, Mensaje:, etc.)
const FIELD_LABEL_RE = /^([\p{L}][\p{L}\s]{0,16}):$/u;

// Empareja "Campo:" + valor (en el mismo párrafo o en el siguiente) → bloque field.
function coalesceFields(blocks) {
  const out = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (b.t === "p" && b.lines.length) {
      const first = (b.lines[0] || "").trim();
      if (FIELD_LABEL_RE.test(first)) {
        const label = first.replace(/:$/, "");
        if (b.lines.length > 1) {
          out.push({ t: "field", label, lines: b.lines.slice(1) });
          continue;
        }
        const next = blocks[i + 1];
        if (next && next.t === "p") {
          out.push({ t: "field", label, lines: next.lines });
          i++;
          continue;
        }
        out.push({ t: "field", label, lines: [] });
        continue;
      }
    }
    out.push(b);
  }
  return out;
}

function splitQuoted(text) {
  const lines = text.split("\n");
  const markers = [/^>\s?/, /^On .+wrote:$/i, /^El .+escribi[oó]:$/i, /^-{2,}\s*Original Message\s*-{2,}/i, /^_{5,}\s*$/];
  for (let i = 1; i < lines.length; i++) {
    const l = lines[i].trim();
    if (markers.some((re) => re.test(l))) {
      const main = lines.slice(0, i).join("\n").trim();
      if (main !== "") return { main, quoted: lines.slice(i).join("\n").trim() };
    }
  }
  return { main: text, quoted: null };
}

// Preview de la lista: markdown → texto plano; para avisos de contacto salta al "Mensaje:".
function plainPreview(content, channel) {
  if (!content) return "(sin contenido)";
  let t = channel === "email" ? cleanEmailText(content) : content;
  t = t
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/^>\s?/gm, "");
  const mm = t.match(/(?:^|\n)\s*(?:Mensaje|Message)\s*:\s*\n*([\s\S]+)/i);
  if (mm && mm[1].trim().length > 8) t = mm[1];
  return t.replace(/\s+/g, " ").trim() || "(sin contenido)";
}

// raw_payload puede venir como objeto o string JSON según el serializador.
function getRawPayload(m) {
  let rp = m && m.raw_payload;
  if (typeof rp === "string") { try { rp = JSON.parse(rp); } catch (e) { rp = null; } }
  return rp || null;
}
// Decodifica nombres MIME-encoded (RFC 2047: "=?UTF-8?B?...?=" / "=?...?Q?...?=").
// El remitente a veces llega así y se mostraba crudo como título.
function decodeMimeHeader(s) {
  s = (s == null ? "" : String(s));
  if (s.indexOf("=?") === -1) return s;
  const decoded = s.replace(/=\?[^?]+\?([BbQq])\?([^?]*)\?=/g, (match, enc, text) => {
    try {
      let bin;
      if (enc.toUpperCase() === "B") {
        bin = atob(text.replace(/\s+/g, ""));
      } else {
        bin = text.replace(/_/g, " ").replace(/=([0-9A-Fa-f]{2})/g, (mm, h) => String.fromCharCode(parseInt(h, 16)));
      }
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      return new TextDecoder("utf-8").decode(bytes);
    } catch (e) {
      return match;
    }
  });
  // Palabras codificadas adyacentes van pegadas (sin el espacio que las separa).
  return decoded.replace(/\?=\s+=\?/g, "").trim() || s;
}

// País del cliente — dato destacado para el tag. Prioriza el país AUTORITATIVO que resuelve
// el backend (cuenta → formulario → texto), guardado en ai_payment_hints.country, para que el
// tag sea consistente en lista/cabecera/respuesta; cae al formulario de contacto si no está.
function customerCountry(m) {
  let h = m && m.ai_payment_hints;
  if (typeof h === "string") { try { h = JSON.parse(h); } catch (e) { h = null; } }
  if (h && typeof h === "object" && h.country) return h.country;
  const cf = getRawPayload(m) && getRawPayload(m).contact_form;
  return (cf && (cf.country || cf.origin)) || null;
}

// Nombre de país (en español) → código ISO-2, para mostrar la banderita real en el tag.
const COUNTRY_ISO = {
  "argentina": "AR", "mexico": "MX", "espana": "ES", "peru": "PE", "colombia": "CO",
  "uruguay": "UY", "chile": "CL", "venezuela": "VE", "ecuador": "EC", "bolivia": "BO",
  "paraguay": "PY", "brasil": "BR", "brazil": "BR", "costa rica": "CR", "panama": "PA",
  "guatemala": "GT", "honduras": "HN", "el salvador": "SV", "nicaragua": "NI",
  "republica dominicana": "DO", "puerto rico": "PR", "cuba": "CU",
  "estados unidos": "US", "usa": "US", "united states": "US", "canada": "CA",
  "alemania": "DE", "francia": "FR", "italia": "IT", "portugal": "PT", "espana ": "ES",
  "reino unido": "GB", "inglaterra": "GB", "andorra": "AD", "suiza": "CH",
  "paises bajos": "NL", "holanda": "NL", "belgica": "BE", "austria": "AT", "irlanda": "IE",
};
// Bandera emoji (colores reales del país en orden correcto) a partir del nombre. "" si no hay match.
function countryFlag(name) {
  if (!name) return "";
  const k = name.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const iso = COUNTRY_ISO[k];
  if (!iso) return "";
  return iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Tamaño legible de un adjunto.
function fmtSize(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + " KB";
  return (b / (1024 * 1024)).toFixed(1) + " MB";
}

// Adjuntos de un mensaje (comprobantes de pago, etc.). Imágenes → miniatura clickeable;
// el resto → chip con nombre/tamaño. El backend los sirve por /admin/inbox/attachment/{id}/{idx}.
// Sólo un audio suena a la vez: al arrancar uno se pausa el que estaba sonando.
// Vive fuera del componente porque tiene que ser compartido por todas las burbujas.
let playingAudioEl = null;

function fmtDur(s) {
  if (!isFinite(s) || s < 0) return null;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/**
 * Reproductor inline para notas de voz y adjuntos de audio.
 *
 * Las notas de voz de WhatsApp son Opus en contenedor Ogg. NO gateamos con
 * canPlayType: Safari devuelve "" para audio/ogg y sin embargo lo reproduce
 * (CoreAudio de macOS decodifica Ogg/Opus). Así que intentamos siempre y sólo
 * si el <audio> tira error mostramos el link de descarga de antes.
 */
function AudioAttachment({ url, name, size, isOut, fallback }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(NaN);
  const [rate, setRate] = useState(1);
  const [failed, setFailed] = useState(false);

  // Al desmontarse (cambio de conversación) hay que soltar el audio: si no,
  // sigue sonando una nota de voz de un hilo que ya no está en pantalla.
  useEffect(() => () => {
    const el = ref.current;
    if (el) {
      el.pause();
      if (playingAudioEl === el) playingAudioEl = null;
    }
  }, []);

  if (failed) return fallback;

  const fg = isOut ? "#fff" : "#334155";
  const dim = isOut ? "rgba(255,255,255,0.75)" : "#64748B";
  const track = isOut ? "rgba(255,255,255,0.28)" : "#E2E8F0";
  const fill = isOut ? "#fff" : "#4338CA";
  const border = isOut ? "rgba(255,255,255,0.35)" : "#E2E8F0";
  const bg = isOut ? "rgba(255,255,255,0.12)" : "#F8FAFC";

  const pct = isFinite(dur) && dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;
  const curLabel = fmtDur(cur) || "0:00";
  const durLabel = fmtDur(dur);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      if (playingAudioEl && playingAudioEl !== el) playingAudioEl.pause();
      playingAudioEl = el;
      el.playbackRate = rate;
      // play() rechaza si el archivo no se puede decodificar; ahí mostramos el fallback.
      const p = el.play();
      if (p && p.catch) p.catch(() => setFailed(true));
    } else {
      el.pause();
    }
  };

  const seek = (e) => {
    const el = ref.current;
    if (!el || !isFinite(dur) || dur <= 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    el.currentTime = Math.max(0, Math.min(dur, ((e.clientX - r.left) / r.width) * dur));
  };

  const cycleRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (ref.current) ref.current.playbackRate = next;
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, marginTop: 8,
      background: bg, border: `1px solid ${border}`, borderRadius: 10,
      padding: "8px 10px", minWidth: 260, maxWidth: 340, boxSizing: "border-box",
    }}>
      <audio
        ref={ref}
        src={url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCur(0); }}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        // Ogg sin cabecera de duración: algunos navegadores la resuelven recién
        // al terminar de bajar el archivo.
        onDurationChange={(e) => setDur(e.currentTarget.duration)}
        onError={() => setFailed(true)}
      />
      <button
        onClick={toggle}
        title={playing ? "Pausar" : "Reproducir"}
        style={{
          flexShrink: 0, width: 34, height: 34, borderRadius: "50%", cursor: "pointer",
          border: "none", background: isOut ? "rgba(255,255,255,0.9)" : "#4338CA",
          color: isOut ? "#4338CA" : "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
        <Icon name={playing ? "pause" : "play"} size={15} />
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={seek}
          title={isFinite(dur) ? "Clic para moverte" : name}
          style={{
            height: 5, borderRadius: 999, background: track,
            cursor: isFinite(dur) && dur > 0 ? "pointer" : "default", overflow: "hidden",
          }}>
          <div style={{ width: `${pct}%`, height: "100%", background: fill, borderRadius: 999 }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5, fontSize: 11, color: dim }}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {curLabel}{durLabel ? ` / ${durLabel}` : ""}
          </span>
          <span style={{ opacity: 0.6 }}>·</span>
          <span>{fmtSize(size)}</span>
        </div>
      </div>

      <button
        onClick={cycleRate}
        title="Velocidad de reproducción"
        style={{
          flexShrink: 0, cursor: "pointer", background: "transparent",
          border: `1px solid ${border}`, borderRadius: 999, color: fg,
          padding: "2px 7px", fontSize: 11, fontWeight: 700,
        }}>
        {rate}×
      </button>
      <a href={`${url}?dl=1`} target="_blank" rel="noreferrer" title={`Descargar ${name}`}
        style={{ flexShrink: 0, display: "inline-flex", color: dim }}>
        <Icon name="download" size={14} color={dim} />
      </a>
    </div>
  );
}

function Attachments({ msg, isOut }) {
  const list = Array.isArray(msg.media) ? msg.media : [];
  if (!list.length) return null;
  const fg = isOut ? "#DCE3FF" : "#334155";
  const border = isOut ? "rgba(255,255,255,0.35)" : "#E2E8F0";
  const bg = isOut ? "rgba(255,255,255,0.12)" : "#F8FAFC";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
      {list.map((a) => {
        const url = `${API_URI}/admin/inbox/attachment/${msg.id}/${a.idx}`;
        const isImg = (a.mime || "").indexOf("image/") === 0;
        if (isImg) {
          return (
            <a key={a.idx} href={url} target="_blank" rel="noreferrer" title={`${a.name} · ${fmtSize(a.size)}`}
              style={{ display: "block", lineHeight: 0 }}>
              <img src={url} alt={a.name}
                style={{ maxWidth: 180, maxHeight: 180, borderRadius: 8, border: `1px solid ${border}`, objectFit: "cover" }} />
            </a>
          );
        }
        const link = (
          <a key={a.idx} href={`${url}?dl=1`} target="_blank" rel="noreferrer" title={`Descargar ${a.name}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none",
              color: fg, background: bg, border: `1px solid ${border}`, borderRadius: 8,
              padding: "7px 10px", maxWidth: 240,
            }}>
            <Icon name="file" size={16} color={fg} />
            <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
              <span style={{ fontSize: 10, opacity: 0.7 }}>{a.is_receipt ? "✓ comprobante" : fmtSize(a.size)}</span>
            </span>
            <Icon name="download" size={14} color={fg} />
          </a>
        );

        // Notas de voz de WhatsApp y cualquier audio adjunto: se escuchan acá mismo.
        // Si el navegador no puede decodificarlo, el reproductor cae al link de descarga.
        if ((a.mime || "").indexOf("audio/") === 0) {
          return (
            <AudioAttachment key={a.idx} url={url} name={a.name} size={a.size}
              isOut={isOut} fallback={link} />
          );
        }

        return link;
      })}
    </div>
  );
}

// Componente: cuerpo del mensaje renderizado + cadena citada colapsable.
function MessageBody({ content, isOut, channel, originalBody, hasMedia }) {
  const [showQuote, setShowQuote] = useState(false);
  const [showFull, setShowFull] = useState(false);
  // Si no hay texto pero sí adjunto (ej. imagen sin caption), no mostramos el
  // placeholder "(sin contenido)": el adjunto se renderiza aparte.
  if (!content || !content.trim()) return hasMedia ? null : <span style={{ opacity: 0.55 }}>(sin contenido)</span>;
  const linkColor = isOut ? "#DCE3FF" : "#4F46E5";
  const hasFull = channel === "email" && originalBody && originalBody.trim() && originalBody.trim() !== content.trim();

  // Mostrar la conversación completa (original con la cadena citada).
  if (showFull && hasFull) {
    return (
      <div style={{ whiteSpace: "normal" }}>
        <button
          onClick={() => setShowFull(false)}
          style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: 8, fontSize: 11, fontWeight: 600, color: linkColor, textDecoration: "underline" }}>
          ← Ver solo el mensaje
        </button>
        {renderBlocks(parseBlocks(cleanEmailText(originalBody, { full: true })), isOut, "f")}
      </div>
    );
  }

  let main = content, quoted = null;
  if (channel === "email") {
    const cleaned = cleanEmailText(content);
    const sp = splitQuoted(cleaned);
    main = sp.main || cleaned;
    quoted = sp.quoted;
  }
  let mainBlocks = parseBlocks(main);
  if (channel === "email") mainBlocks = coalesceFields(mainBlocks);
  return (
    <div style={{ whiteSpace: "normal" }}>
      {renderBlocks(mainBlocks, isOut, "m")}
      {quoted && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => setShowQuote((v) => !v)}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, fontSize: 11, fontWeight: 600, color: linkColor, textDecoration: "underline" }}>
            {showQuote ? "Ocultar texto citado" : "Mostrar texto citado"}
          </button>
          {showQuote && <div style={{ marginTop: 6, opacity: 0.8, fontSize: 12.5 }}>{renderBlocks(parseBlocks(quoted), isOut, "q")}</div>}
        </div>
      )}
      {hasFull && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => setShowFull(true)}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, fontSize: 11, fontWeight: 600, color: linkColor, textDecoration: "underline" }}>
            Ver conversación completa
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  Component
// ────────────────────────────────────────────────────────────────────
export default function Inbox() {
  const classes = useStyles();
  const [filter, setFilter] = useState({ channel: "all", mailbox: "all", kind: "all", status: "pending", sentiment: "all", sales: false, hostile: false, postAgg: true, important: false });
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  // selectedId está sincronizado con el hash de la URL así se puede compartir.
  // Formato: /#/inbox/123 → selected=123. /#/inbox → null.
  const parseSelectedFromHash = () => {
    const m = (window.location.hash || "").match(/^#\/inbox\/(\d+)/);
    return m ? Number(m[1]) : null;
  };
  const [selectedId, setSelectedIdState] = useState(parseSelectedFromHash());
  // Cajón lateral (sólo celular). Arranca cerrado.
  const [navOpen, setNavOpen] = useState(false);
  const setSelectedId = (id) => {
    setSelectedIdState(id);
    const base = "#/inbox";
    const next = id ? `${base}/${id}` : base;
    if (window.location.hash !== next) {
      // pushState para que sume al historial y "atrás" cierre el detalle.
      window.history.pushState(null, "", next);
    }
  };
  useEffect(() => {
    const onPop = () => setSelectedIdState(parseSelectedFromHash());
    window.addEventListener("hashchange", onPop);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("hashchange", onPop);
      window.removeEventListener("popstate", onPop);
    };
  }, []);
  const [detail, setDetail] = useState(null);
  const [editedReply, setEditedReply] = useState("");
  const [regenOpen, setRegenOpen] = useState(false);
  const [sigPreview, setSigPreview] = useState(false);
  // Asistente de activación (pagó y no pudo crear cuenta).
  const [activation, setActivation] = useState(null);
  const [activationBusy, setActivationBusy] = useState(false);
  // Los pagos para renovar se muestran plegados: hay alumnos con 25, y esa lista empujaba
  // el mensaje del cliente fuera de la pantalla. Ver el mensaje es lo primero que hace
  // falta para poder contestarlo; los pagos se abren cuando se los va a usar.
  const [renovarAbierto, setRenovarAbierto] = useState(false);
  // El panel de cuenta/pagos arranca PLEGADO en celular y abierto en escritorio. En una
  // pantalla de 844px de alto ese panel dejaba el hilo de mensajes en 40px: se veía todo
  // menos la conversación. En escritorio no molesta a nadie, así que ahí no cambia nada.
  const [actPanelAbierto, setActPanelAbierto] = useState(() => !esCelular());
  // Confirmación visible de la última acción de activación/renovación sobre este mensaje.
  const [activationDone, setActivationDone] = useState(null); // { mode, end, mail, student_id }
  const fetchActivation = (id) => {
    fetch(API_URI + "/admin/inbox/" + id + "/activation")
      .then((r) => r.json())
      .then((res) => {
        if (res && res.result) {
          setActivation(res);
          // Prefilla el correo de la cuenta con el detectado (en WhatsApp/DM sale del hilo),
          // sin pisar lo que el operador ya haya escrito.
          setActivationEmailInput((cur) => cur || (res.customer_email || ""));
        }
      })
      .catch(() => {});
  };
  // Correo con el que se creará/activará la cuenta al "Activar nuevo". Editable por el operador
  // (clave en WhatsApp, donde el mail no viene en el handle sino tipeado en el chat).
  const [activationEmailInput, setActivationEmailInput] = useState("");
  // Altura del editor de respuesta: redimensible por el usuario y persistida (localStorage).
  const [editorHeight, setEditorHeight] = useState(() => {
    try { const v = parseInt(localStorage.getItem("inboxEditorHeight"), 10); if (v && v >= 80) return v; } catch (e) {}
    return 220;
  });
  // Popups del panel de comandos: "ver detalles" (todos los pagos) y "buscar alumno".
  const [detailsModal, setDetailsModal] = useState(null);   // { loading, student, payments, error }
  // Buscador de pagos para asociar a un alumno y renovar (dentro de "ver pagos").
  const [assoc, setAssoc] = useState({ q: "", results: [], busy: false, actingId: null, status: "confirmed", country: "all" });
  const [trace, setTrace] = useState(null);                 // traza IA: { loading } | { data } | { error }
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchActive, setSearchActive] = useState("all");  // all | active | inactive
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchTouched, setSearchTouched] = useState(false);
  const [manualAccount, setManualAccount] = useState(null); // alumno elegido a mano (override de la cuenta detectada)
  const [paysOpen, setPaysOpen] = useState(false);          // mostrar pagos ya vinculados (colapsados por defecto)
  // Buscador rápido de pagos (popup) — mismo backend que /admin/payments.
  const [payOpen, setPayOpen] = useState(false);
  const [paySearch, setPaySearch] = useState("");
  const [payResults, setPayResults] = useState([]);
  const [payBusy, setPayBusy] = useState(false);
  const [payTotal, setPayTotal] = useState(null);
  const [payStatus, setPayStatus] = useState("all"); // all | confirmed | assigned
  const [payCountry, setPayCountry] = useState("all"); // "all" | country_id
  // Crear alumno + activar desde un pago CONFIRMADO del buscador → abre un mensaje nuevo.
  const [activatePay, setActivatePay] = useState(null); // { p, email, name, lastname, preview, loadingPreview, busy, error }
  // Crear alumno SIN pago (alta manual) → abre un mensaje nuevo de activación.
  const [manualNew, setManualNew] = useState(null); // { email, name, lastname, country_id, years, busy, error }
  const [countries, setCountries] = useState([]);
  // Cargar países (para el filtro del buscador y el alta manual) al abrir el popup de pagos.
  useEffect(() => {
    if (payOpen && countries.length === 0) {
      fetch(API_URI + "/admin/cfg/countries").then((r) => r.json()).then((d) => setCountries(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [payOpen, countries.length]);
  useEffect(() => {
    if (!payOpen) return undefined;
    const t = setTimeout(() => {
      const body = new FormData();
      if (paySearch.trim()) body.append("search", paySearch.trim());
      if (payStatus === "confirmed" || payStatus === "assigned") body.append("status", payStatus);
      if (payCountry !== "all") body.append("country_id", payCountry);
      setPayBusy(true);
      fetch(`${API_URI}/admin/payments`, { method: "POST", body })
        .then((r) => r.json())
        .then((res) => {
          setPayBusy(false);
          setPayResults((res && res.payments && res.payments.data) || []);
          setPayTotal(res ? res.total : null);
        })
        .catch(() => { setPayBusy(false); setPayResults([]); });
    }, 350);
    return () => clearTimeout(t);
  }, [paySearch, payStatus, payCountry, payOpen]);
  const openDetails = (studentId, defaultCountryId) => {
    if (!studentId) return;
    setAssoc({ q: "", results: [], busy: false, actingId: null, status: "confirmed", country: defaultCountryId ? String(defaultCountryId) : "all" });
    if (countries.length === 0) {
      fetch(API_URI + "/admin/cfg/countries").then((r) => r.json()).then((d) => setCountries(Array.isArray(d) ? d : [])).catch(() => {});
    }
    setDetailsModal({ loading: true });
    fetch(API_URI + "/admin/inbox/student/" + studentId + "/payments")
      .then((r) => r.json())
      .then((res) => setDetailsModal(res && res.result ? { loading: false, student: res.student, payments: res.payments } : { loading: false, error: "No se pudo cargar" }))
      .catch(() => setDetailsModal({ loading: false, error: "Error de red" }));
  };
  // Buscar pagos para asociarlos al alumno del modal "ver pagos" (filtros: estado + país).
  useEffect(() => {
    if (!detailsModal || !detailsModal.student) return undefined;
    const q = assoc.q.trim();
    // Busca si hay texto (>=2) o si hay un país elegido (permite navegar los del país del contacto).
    if (q.length < 2 && assoc.country === "all") { setAssoc((s) => ({ ...s, results: [] })); return undefined; }
    const t = setTimeout(() => {
      setAssoc((s) => ({ ...s, busy: true }));
      const body = new FormData();
      if (q.length >= 2) body.append("search", q);
      if (assoc.status === "confirmed" || assoc.status === "assigned") body.append("status", assoc.status);
      if (assoc.country !== "all") body.append("country_id", assoc.country);
      fetch(`${API_URI}/admin/payments`, { method: "POST", body })
        .then((r) => r.json())
        .then((res) => setAssoc((s) => ({ ...s, busy: false, results: (res && res.payments && res.payments.data) || [] })))
        .catch(() => setAssoc((s) => ({ ...s, busy: false, results: [] })));
    }, 350);
    return () => clearTimeout(t);
  }, [assoc.q, assoc.status, assoc.country, detailsModal]);
  // Asociar un pago al alumno y renovar/extender su membresía.
  const assocRenew = async (p) => {
    const stu = detailsModal && detailsModal.student;
    if (!stu) return;
    const ok = await askConfirm({
      title: "Renovar con este pago",
      text: `Vas a asociar el pago #${p.id}${p.country && p.country.name ? " (" + p.country.name + ")" : ""} a ${stu.name || stu.mail} y extender su membresía. ¿Continuar?`,
      confirmLabel: "Renovar",
    });
    if (!ok) return;
    setAssoc((s) => ({ ...s, actingId: p.id }));
    postJson("/admin/inbox/activation/renew", { student_id: stu.id, subscription_id: p.id }).then((res) => {
      setAssoc((s) => ({ ...s, actingId: null }));
      if (res && res.result) {
        showToast("ok", `Membresía renovada · vence ${res.end}`);
        setDetailsModal(null); // cerramos el modal: abajo se ve el panel y la respuesta actualizados
        // Refrescar el panel de activación del mensaje (vence nuevo + ✓ aplicada) y dejar la
        // respuesta de renovación lista en el composer — sin necesidad de refrescar la página.
        if (detail && detail.message) {
          fetchActivation(detail.message.id);
          if (res.suggested_reply) applyAndSaveSuggestion(res.suggested_reply);
        }
      } else {
        showToast("err", (res && res.error) || "No se pudo renovar");
      }
    }).catch(() => { setAssoc((s) => ({ ...s, actingId: null })); showToast("err", "Error de red"); });
  };
  // Traza del procesamiento IA: qué modelo y qué contexto se usó para armar la respuesta.
  const openTrace = (mid) => {
    if (!mid) return;
    setTrace({ loading: true });
    fetch(API_URI + "/admin/inbox/" + mid + "/ai-trace")
      .then((r) => r.json())
      .then((res) => setTrace(res && res.result ? { loading: false, data: res } : { loading: false, error: "No se pudo cargar la traza" }))
      .catch(() => setTrace({ loading: false, error: "Error de red" }));
  };
  // Abre el formulario "Crear alumno + activar" para un pago confirmado y trae la previsualización
  // (país + cómo quedaría la membresía).
  const openActivatePay = (p) => {
    const pinfo = p.payer_info || {};
    const guessEmail = (p.mail && /@/.test(p.mail)) ? p.mail : ((pinfo.email && /@/.test(pinfo.email)) ? pinfo.email : "");
    setActivatePay({ p, email: guessEmail, name: pinfo.first_name || "", lastname: pinfo.last_name || "", preview: null, loadingPreview: true, busy: false, error: "" });
    fetch(API_URI + "/admin/inbox/payment/" + p.id + "/activation-preview")
      .then((r) => r.json())
      .then((res) => setActivatePay((s) => {
        if (!s || s.p.id !== p.id) return s;
        const pay = (res && res.result) ? res.payment : null;
        return {
          ...s, loadingPreview: false, preview: pay,
          email: s.email || (pay && pay.email) || "",
          name: s.name || (pay && pay.first_name) || "",
          lastname: s.lastname || (pay && pay.last_name) || "",
        };
      }))
      .catch(() => setActivatePay((s) => (s && s.p.id === p.id) ? { ...s, loadingPreview: false } : s));
  };
  // Crea el alumno + asigna el pago + abre un mensaje NUEVO a ese correo con la bienvenida lista.
  const submitActivatePay = () => {
    const a = activatePay;
    if (!a) return;
    const email = (a.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setActivatePay((s) => s ? { ...s, error: "Poné un correo válido." } : s);
      return;
    }
    setActivatePay((s) => s ? { ...s, busy: true, error: "" } : s);
    postJson("/admin/inbox/activation/create-and-compose", {
      subscription_id: a.p.id,
      email,
      name: (a.name || "").trim(),
      lastname: (a.lastname || "").trim(),
      country_id: (a.preview && a.preview.country_id) || a.p.country_id || null,
    }).then((res) => {
      if (res && res.result && res.message_id) {
        showToast("ok", `Alumno ${res.created ? "creado" : "ya existía"} · mensaje de activación listo`);
        setActivatePay(null);
        setPayOpen(false);
        fetchList();
        setSelectedId(res.message_id);
      } else {
        setActivatePay((s) => s ? { ...s, busy: false, error: (res && res.error) || "No se pudo crear" } : s);
      }
    }).catch(() => setActivatePay((s) => s ? { ...s, busy: false, error: "Error de red" } : s));
  };
  // Alta MANUAL sin pago: crea la cuenta + membresía por N años + abre el mensaje de activación.
  const openManualNew = () => {
    setManualNew({ email: "", name: "", lastname: "", country_id: "", years: 5, busy: false, error: "" });
    if (countries.length === 0) {
      fetch(API_URI + "/admin/cfg/countries").then((r) => r.json()).then((d) => setCountries(Array.isArray(d) ? d : [])).catch(() => {});
    }
  };
  const submitManualNew = () => {
    const a = manualNew;
    if (!a) return;
    const email = (a.email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setManualNew((s) => s ? { ...s, error: "Poné un correo válido." } : s);
      return;
    }
    setManualNew((s) => s ? { ...s, busy: true, error: "" } : s);
    postJson("/admin/inbox/activation/create-and-compose", {
      email,
      name: (a.name || "").trim(),
      lastname: (a.lastname || "").trim(),
      country_id: a.country_id || null,
      years: a.years || 5,
    }).then((res) => {
      if (res && res.result && res.message_id) {
        showToast("ok", `Alumno ${res.created ? "creado" : "ya existía"} · activación lista`);
        setManualNew(null);
        setPayOpen(false);
        fetchList();
        setSelectedId(res.message_id);
      } else {
        setManualNew((s) => s ? { ...s, busy: false, error: (res && res.error) || "No se pudo crear" } : s);
      }
    }).catch(() => setManualNew((s) => s ? { ...s, busy: false, error: "Error de red" } : s));
  };
  const runStudentSearch = () => {
    const q = searchQ.trim();
    setSearchTouched(true);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearchBusy(true);
    const params = new URLSearchParams({ q });
    if (searchActive !== "all") params.set("active", searchActive === "active" ? "1" : "0");
    fetch(API_URI + "/admin/inbox/students/search?" + params.toString())
      .then((r) => r.json())
      .then((res) => { setSearchBusy(false); setSearchResults((res && res.students) || []); })
      .catch(() => { setSearchBusy(false); setSearchResults([]); });
  };
  const pickStudent = (s) => {
    setManualAccount({ exists: true, student_id: s.id, name: s.name, mail: s.mail, state: s.state, country_id: s.country_id, current_end: s.current_end, active: s.active });
    setSearchOpen(false);
  };
  // Candidato sugerido (por nombre / mail del cuerpo) elegido por el operador.
  const pickCandidate = (c) => setManualAccount({ exists: true, student_id: c.student_id, name: c.name, mail: c.mail, state: c.state, country_id: c.country_id, country: c.country, current_end: c.current_end, active: c.active });
  // Estilos compartidos de los popups del panel.
  const _ovl = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
  const _box = { background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxWidth: "92vw", maxHeight: "86vh", overflow: "hidden", display: "flex", flexDirection: "column" };
  const _mhead = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #E2E8F0" };
  const _mtitle = { fontSize: 15, fontWeight: 800, color: "#0F172A" };
  const _mclose = { background: "none", border: 0, fontSize: 18, color: "#64748B", cursor: "pointer", lineHeight: 1 };
  const _th = { padding: "6px 10px", fontWeight: 700, fontSize: 12, textAlign: "left", whiteSpace: "nowrap" };
  const _td = { padding: "6px 10px", whiteSpace: "nowrap" };
  // Editor enriquecido (contentEditable): Cmd+B / Cmd+I nativos, sin barra.
  const editorRef = useRef(null);
  // Redimensionar el editor arrastrando su BORDE SUPERIOR: arriba = más alto, abajo = más bajo.
  const startEditorResize = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = editorRef.current ? editorRef.current.offsetHeight : editorHeight;
    const onMove = (ev) => {
      const h = Math.min(600, Math.max(80, startH + (startY - ev.clientY)));
      setEditorHeight(h);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const h = editorRef.current ? editorRef.current.offsetHeight : editorHeight;
      try { localStorage.setItem("inboxEditorHeight", String(h)); } catch (e2) {}
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  // Markdown mínimo → HTML para el editor (el componente envía mail HTML): **negrita**
  // + links clickeables ([texto](url) y URLs sueltas → <a>).
  const stripMd = (t) => String(t || "")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1: $2")
    .replace(/\*\*(.+?)\*\*/g, "$1");
  const htmlForEditor = (text) => {
    if (!text) return "";
    let s = String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Link markdown [texto](url) → <a> clickeable.
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    // Negrita.
    s = s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    // Autolink de URLs sueltas (que no quedaron ya dentro de un href).
    s = s.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');
    return s.split("\n").map((l) => "<div>" + (l === "" ? "<br>" : l) + "</div>").join("");
  };
  const applyToEditor = (text) => {
    if (editorRef.current) editorRef.current.innerHTML = htmlForEditor(text);
    // editedReply es el texto plano de fallback: sin los marcadores **.
    setEditedReply(stripMd(text));
  };
  // Carga la respuesta en el editor Y la PERSISTE como ai_suggested_reply del mensaje,
  // para que siga ahí al salir y volver (la guarda con los ** crudos → al recargar
  // htmlForEditor los convierte a negrita).
  const applyAndSaveSuggestion = (text) => {
    if (!detail || !text) return;
    applyToEditor(text);
    postJson("/admin/inbox/update-suggestion", { id: detail.message.id, suggested_reply: text });
    setDetail((d) => (d && d.message ? { ...d, message: { ...d.message, ai_suggested_reply: text } } : d));
  };
  // Cargar el editor cuando cambia el mensaje seleccionado (con la sugerencia IA).
  useEffect(() => {
    if (!detail || !editorRef.current) return;
    const already = detail.message.status === "replied";
    editorRef.current.innerHTML = htmlForEditor(already ? "" : (detail.message.ai_suggested_reply || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail && detail.message && detail.message.id]);
  // Panel de comandos: SIEMPRE intentamos detectar alumno + pagos para cualquier
  // email (no solo cuando hay pedido de activación), así está siempre a mano.
  useEffect(() => {
    setActivation(null);
    setActivationDone(null);
    setManualAccount(null);
    setPaysOpen(false);
    setImproveOpen(false);
    setImproveHint("");
    setDetailsModal(null);
    setSearchOpen(false);
    setSearchResults([]);
    setSearchQ("");
    setSearchTouched(false);
    setActivationEmailInput("");
    const m = detail && detail.message;
    // Traemos el asistente de activación (alumno + pagos) en email y también en WhatsApp/DM
    // (conversaciones directas 1:1 donde se activa/renueva), no en comentarios públicos.
    const isDmT = detail && detail.thread && detail.thread.parent_ref && detail.thread.parent_ref.kind === "dm";
    if (m && (m.channel === "email" || m.channel === "whatsapp" || isDmT)) fetchActivation(m.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail && detail.message && detail.message.id]);
  const [regenHint, setRegenHint] = useState("");
  const [improveOpen, setImproveOpen] = useState(false);     // cajita de instrucciones para "Mejorar"
  const [improveHint, setImproveHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // qué acción lenta corre (para el spinner del botón)
  useEffect(() => { if (!busy) setBusyAction(null); }, [busy]);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncMsg, setSyncMsg] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  // ── Base de conocimiento (KB) ────────────────────────────
  const emptyKbForm = { id: null, title: "", type: "example", mailbox: "all", channel: "all", customer_message: "", ideal_reply: "", content: "", tags: "", priority: 0, is_active: true };
  const [kbOpen, setKbOpen] = useState(false);
  const [kbItems, setKbItems] = useState([]);
  const [kbBusy, setKbBusy] = useState(false);
  const [kbForm, setKbForm] = useState(emptyKbForm);
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const templatesRef = useRef(null);
  // Posición (fixed) de los dropdowns del composer: la botonera tiene overflow-x:auto, que clipea
  // un dropdown posicionado absolute → no se veía. Con position:fixed escapan el recorte.
  const [templatesPos, setTemplatesPos] = useState(null);
  const [morePos, setMorePos] = useState(null);
  const dropPos = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { right: Math.max(8, window.innerWidth - r.right), bottom: Math.max(8, window.innerHeight - r.top + 6) };
  };
  const fixedPopStyle = (pos) => ({ position: "fixed", right: pos ? pos.right : 16, bottom: pos ? pos.bottom : 64, left: "auto", top: "auto", zIndex: 9999 });
  const [kbFilter, setKbFilter] = useState("all");
  const [kbFormOpen, setKbFormOpen] = useState(false);
  // ── Spam: reglas de remitentes bloqueados ────────────────
  // Ojo: esto es SOLO del panel. No mueve, borra ni marca nada en la casilla de
  // correo real — el mail sigue llegando igual, sólo cambia dónde lo vemos acá.
  const [spamOpen, setSpamOpen] = useState(false);
  const [spamRules, setSpamRules] = useState([]);
  const [spamBusy, setSpamBusy] = useState(false);
  const [spamSearch, setSpamSearch] = useState("");
  const [spamDlg, setSpamDlg] = useState(null); // mensaje que se está por marcar
  const [toast, setToast] = useState(null); // { kind: 'ok'|'err', text }
  const toastTimer = useRef(null);
  const showToast = (kind, text) => {
    setToast({ kind, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4500);
  };

  // Modal de confirmación: { title, text, danger, confirmLabel, onConfirm }
  const [confirmDlg, setConfirmDlg] = useState(null);
  const askConfirm = (opts) => new Promise((resolve) => {
    setConfirmDlg({
      ...opts,
      onResolve: (ok) => { setConfirmDlg(null); resolve(ok); },
    });
  });
  const searchTimer = useRef(null);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [moreOpen]);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const PER_PAGE = 50;

  const fetchList = useCallback((pageToLoad = 1, append = false) => {
    const body = {
      sentiment: filter.sentiment,
      page: pageToLoad,
      per_page: PER_PAGE,
    };
    if (filter.important) {
      // Bandeja "Importante": global y ortogonal — todo lo marcado, de cualquier
      // canal y status (no aplicamos channel/status/curated).
      body.important = 1;
    } else {
      body.channel = filter.channel;
      body.status = filter.status;
      if (filter.mailbox && filter.mailbox !== "all") body.mailbox = filter.mailbox;
      if (filter.sales) body.sales_intent_min = 0.5;
      if (filter.hostile) body.hostility_min = 0.45;
      if (filter.kind && filter.kind !== "all") body.kind = filter.kind;
      // En comentarios, por defecto solo agresivos (mismo criterio que el badge:
      // dirigido a Mario/TCDG, o a terceros si es muy alto). El toggle "Todos" lo desactiva.
      if (filter.kind === "comment" && filter.postAgg) body.aggressive = 1;
      // "Todos los canales" = vista curada (directos + DMs + comentarios agresivos),
      // lo mismo que suma el badge Pendientes (no todo el pendiente crudo).
      // La carpeta Spam va SIN curar: si el bloqueado comentó en YouTube sin ser
      // agresivo, la vista curada lo escondería y no habría cómo revisarlo.
      if (filter.channel === "all" && filter.status !== "spam") body.curated = 1;
      if (filter.showIgnored) body.include_ignored = 1;
    }
    if (search) body.search = search;
    postJson("/admin/inbox/list", body).then((res) => {
      if (res.result) {
        setItems((prev) => append ? [...prev, ...res.data] : res.data);
        setCounts(res.counts || {});
        const pg = res.pagination || {};
        setHasMore((pg.current_page || 1) < (pg.last_page || 1));
        setPage(pg.current_page || pageToLoad);
      }
    });
  }, [filter, search]);

  const loadMore = () => fetchList(page + 1, true);

  useEffect(() => { fetchList(1, false); }, [fetchList]);
  useEffect(() => {
    const t = setInterval(() => fetchList(1, false), 15000);
    return () => clearInterval(t);
  }, [fetchList]);

  // Infinite scroll: cuando el contenedor de la lista llega cerca del fondo,
  // cargamos la siguiente tanda automáticamente.
  const listRef = useRef(null);
  const loadingMoreRef = useRef(false);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (loadingMoreRef.current || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        loadingMoreRef.current = true;
        fetchList(page + 1, true);
        // fetchList no devuelve promesa; soltamos el lock después de un breve delay.
        setTimeout(() => { loadingMoreRef.current = false; }, 800);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, page, fetchList]);

  // Navegación con teclado: ↑ / ↓ se mueven entre items de la lista visible.
  useEffect(() => {
    const onKey = (e) => {
      // Evitar interferir con typing en inputs/textareas.
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      if (!items.length) return;
      e.preventDefault();
      const idx = selectedId ? items.findIndex((m) => m.id === selectedId) : -1;
      let next;
      if (e.key === "ArrowDown") next = idx < items.length - 1 ? items[idx + 1] : items[items.length - 1];
      else                       next = idx > 0 ? items[idx - 1] : items[0];
      if (next) setSelectedId(next.id);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [items, selectedId]);

  // Scroll automático: si el item seleccionado queda fuera de viewport, lo traemos.
  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-msg-id="${selectedId}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  const fetchDetail = useCallback((id, opts = {}) => {
    fetch(API_URI + "/admin/inbox/" + id)
      .then((r) => r.json().then((res) => ({ ok: r.ok, res })).catch(() => ({ ok: false, res: null })))
      .then(({ ok, res }) => {
        if (ok && res && res.result) {
          setDetail(res);
          if (!opts.keepEditedReply) {
            // Si el mensaje ya fue respondido, no volver a precargar la sugerencia.
            const already = res.message.status === "replied";
            setEditedReply(already ? "" : (res.message.ai_suggested_reply || ""));
          }
        } else {
          // No dejar la pantalla en blanco sin feedback (antes fallaba en silencio).
          showToast("err", "No se pudo abrir el mensaje" + (res && res.error ? `: ${res.error}` : ""));
        }
      })
      .catch(() => showToast("err", "No se pudo abrir el mensaje (error de red)"));
  }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
    else { setDetail(null); setEditedReply(""); }
  }, [selectedId, fetchDetail]);

  const onSearch = (v) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v), 250);
  };

  const doReply = () => {
    const plain = editorRef.current ? editorRef.current.innerText : editedReply;
    if (!plain.trim() || !detail) return;
    setBusy(true); setBusyAction("reply");
    const sentId = detail.message.id;
    const body = { id: sentId, text: plain };
    if (detail.message.channel === "email" && editorRef.current) body.html = editorRef.current.innerHTML;
    postJson("/admin/inbox/reply", body).then((res) => {
      setBusy(false);
      if (res.result) {
        applyToEditor("");
        showToast("ok", "Respuesta enviada.");
        // Pasar al SIGUIENTE mensaje disponible de la lista (el de abajo y, si no hay, el de
        // arriba); si no queda ninguno, dejar el detalle vacío. El recién respondido sale de la
        // bandeja al refrescar.
        const i = items.findIndex((x) => x.id === sentId);
        const nextItem = i >= 0 ? (items[i + 1] || items[i - 1] || null) : null;
        fetchList();
        setSelectedId(nextItem ? nextItem.id : null);
      } else {
        alert(res.error || "Error al enviar");
      }
    });
  };

  // Texto para confirmación previa y para el toast de éxito/error.
  // `external` = true cuando la acción golpea la API de la red social.
  const actionCopy = (action, channelKey) => {
    const chLabel = (CHANNEL[channelKey] && CHANNEL[channelKey].label) || "la red social";
    switch (action) {
      case "hide":   return { title: "Ocultar comentario",   text: `Se ocultará en ${chLabel}. El autor seguirá viéndolo, pero los demás no.`, confirmLabel: "Ocultar",   danger: false, ok: `Comentario ocultado en ${chLabel}.` };
      case "delete": return { title: "Eliminar comentario",  text: `Se eliminará definitivamente en ${chLabel}. No se puede deshacer.`,        confirmLabel: "Eliminar",  danger: true,  ok: `Comentario eliminado en ${chLabel}.` };
      case "report": return { title: "Reportar como spam",   text: `Se reportará al autor en ${chLabel} como spam.`,                            confirmLabel: "Reportar",  danger: true,  ok: `Reportado como spam en ${chLabel}.` };
      case "block":  return { title: "Bloquear autor",       text: `El autor quedará bloqueado en ${chLabel} y no podrá comentar más.`,         confirmLabel: "Bloquear",  danger: true,  ok: `Autor bloqueado en ${chLabel}.` };
      case "ignore": return { title: "Ignorar mensaje",      text: `Sale de la bandeja pero la publicación en ${chLabel} no se toca.`,           confirmLabel: "Ignorar",   danger: false, ok: `Marcado como ignorado.` };
      default:       return { title: "Confirmar",            text: `¿Confirmar acción "${action}"?`,                                              confirmLabel: "Confirmar", danger: false, ok: `Acción aplicada.` };
    }
  };

  const doModerate = async (action) => {
    if (!detail) return;
    const copy = actionCopy(action, detail.message.channel);
    if (action !== "ignore") {
      const ok = await askConfirm(copy);
      if (!ok) return;
    }
    setBusy(true);
    postJson("/admin/inbox/moderate", { id: detail.message.id, action }).then((res) => {
      setBusy(false);
      if (res.result) {
        showToast("ok", copy.ok);
        fetchList();
        setSelectedId(null);
      } else {
        showToast("err", res.error || "Falló la operación");
      }
    });
  };

  // Cierra la conversación de WhatsApp: el próximo mensaje del contacto vuelve a
  // disparar la autorespuesta de bienvenida (wa_conversations.status='closed').
  const doCloseConversation = async () => {
    if (!detail) return;
    const ok = await askConfirm({
      title: "Cerrar conversación",
      text: "Se cierra esta conversación de WhatsApp y sale de Pendientes. Si el contacto vuelve a escribir, se le enviará de nuevo la autorespuesta de bienvenida.",
      confirmLabel: "Cerrar conversación",
    });
    if (!ok) return;
    setBusy(true);
    postJson("/admin/inbox/whatsapp/close", { id: detail.message.id }).then((res) => {
      setBusy(false);
      if (res.result) { showToast("ok", "Conversación cerrada"); fetchList(); setSelectedId(null); }
      else { showToast("err", res.error || "No se pudo cerrar la conversación"); }
    });
  };

  const quickIgnore = (id, e) => {
    e.stopPropagation();
    const msg = items.find((x) => x.id === id);
    const copy = actionCopy("ignore", msg && msg.channel);
    setItems((prev) => prev.filter((x) => x.id !== id));
    postJson("/admin/inbox/moderate", { id, action: "ignore" }).then((res) => {
      if (!res.result) {
        showToast("err", res.error || "Falló la operación");
        fetchList();
      } else {
        showToast("ok", copy.ok);
        fetchList();
      }
    });
  };

  // Ignorar en bloque lo pendiente de un scope (canal o buzón). scope: { channel?, mailbox?, hostility_min? }
  const doIgnoreAll = async (scope, label) => {
    const ok = await askConfirm({
      title: "Ignorar todos",
      text: `Vas a marcar como ignorado todo lo pendiente de ${label}. Lo podés ver después en el filtro "Ignorados". ¿Continuar?`,
      confirmLabel: "Ignorar todos",
    });
    if (!ok) return;
    setBusy(true);
    postJson("/admin/inbox/ignore-all", scope).then((res) => {
      setBusy(false);
      if (res.result) {
        const n = res.ignored || 0;
        showToast("ok", `${n} ignorado${n === 1 ? "" : "s"} en ${label}`);
        if (res.counts) setCounts(res.counts);
        fetchList();
        setSelectedId(null);
      } else {
        showToast("err", res.error || "Falló la operación");
      }
    });
  };

  const doRegenerate = (hint) => {
    if (!detail) return;
    setBusy(true); setBusyAction("regenerate");
    const prev = detail.message.ai_suggested_reply || "";
    postJson("/admin/inbox/regenerate-reply", { id: detail.message.id, hint: hint || "" }).then((res) => {
      setBusy(false);
      if (!res || res.result === false) {
        showToast("error", "No se pudo regenerar la respuesta");
        return;
      }
      const next = res.ai_suggested_reply || "";
      fetchDetail(detail.message.id);
      if (next) applyToEditor(next);
      setRegenOpen(false);
      setRegenHint("");
      if (!next) {
        showToast("error", "La IA no devolvió respuesta (mensaje sin sugerencia aplicable)");
      } else if (next.trim() === prev.trim()) {
        showToast("ok", "Sugerencia regenerada (sin cambios respecto a la anterior)");
      } else {
        showToast("ok", "Respuesta regenerada");
      }
    }).catch(() => {
      setBusy(false);
      showToast("error", "Error al regenerar la respuesta");
    });
  };

  const doSync = () => {
    if (syncing) return;
    setSyncing(true); setSyncMsg("");
    postJson("/admin/inbox/sync", {}).then((res) => {
      setSyncing(false);
      if (res.result) {
        const parts = [];
        Object.keys(res.results || {}).forEach((k) => {
          const r = res.results[k];
          const out = (r.output || "").split("\n").pop();
          parts.push(`${k}: ${out || (r.ok ? "ok" : "error")}`);
        });
        setLastSync(new Date());
        setSyncMsg(parts.join(" · "));
        fetchList();
      } else { setSyncMsg("Error al sincronizar"); }
    }).catch(() => { setSyncing(false); setSyncMsg("Error al sincronizar"); });
  };

  const saveSuggestion = () => {
    if (!detail) return;
    postJson("/admin/inbox/update-suggestion", { id: detail.message.id, suggested_reply: editedReply });
  };

  // ── Asistente de activación ──────────────────────────────
  const activationEmail = (payment) => {
    const typed = (activationEmailInput || "").trim();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed)) return typed.toLowerCase();
    return (activation && (activation.customer_email || (activation.hints && activation.hints.payer_email)))
      || (payment && payment.mail) || "";
  };

  const doActivateNew = async (payment) => {
    if (!detail || !activation) return;
    const proj = activation.projection || {};
    const email = activationEmail(payment);
    const ok = await askConfirm({
      title: "Activar nuevo alumno",
      text: `Vas a crear la cuenta ${email} (contraseña 1979) y activar la membresía hasta ${proj.new_end || payment.end}. La bienvenida la enviás vos después. ¿Continuar?`,
      confirmLabel: "Activar alumno",
    });
    if (!ok) return;
    setActivationBusy(true);
    postJson("/admin/inbox/activation/activate-new", {
      message_id: detail.message.id, subscription_id: payment.id, email,
      country_id: payment.country_id, start: proj.from, end: proj.new_end,
    }).then((res) => {
      setActivationBusy(false);
      if (res.result) {
        showToast("ok", `Alumno activado · vence ${res.end}`);
        setActivationDone({ mode: "activate", end: res.end, mail: email, student_id: res.student_id });
        if (res.suggested_reply) applyAndSaveSuggestion(res.suggested_reply);
        fetchActivation(detail.message.id);
      } else showToast("err", res.error || "No se pudo activar");
    });
  };

  const doRenew = async (payment) => {
    if (!detail || !activation || !activation.account || !activation.account.exists) return;
    const acc = activation.account; const proj = activation.projection || {};
    const ok = await askConfirm({
      title: "Renovar membresía",
      text: `Vas a extender la membresía de ${acc.mail} hasta ${proj.new_end}. ¿Continuar?`,
      confirmLabel: "Renovar",
    });
    if (!ok) return;
    setActivationBusy(true);
    postJson("/admin/inbox/activation/renew", {
      message_id: detail.message.id, student_id: acc.student_id, subscription_id: payment.id,
      start: proj.from, end: proj.new_end,
    }).then((res) => {
      setActivationBusy(false);
      if (res.result) {
        showToast("ok", `Membresía renovada · vence ${res.end}`);
        setActivationDone({ mode: "renew", end: res.end, mail: acc.mail, student_id: acc.student_id });
        if (res.suggested_reply) applyAndSaveSuggestion(res.suggested_reply);
        fetchActivation(detail.message.id);
      } else showToast("err", res.error || "No se pudo renovar");
    });
  };

  // Generar una contraseña nueva para una cuenta existente (cliente ya activo que no
  // recuerda su clave). Setea la pass y carga la respuesta con sus datos de acceso.
  const doResetPassword = async () => {
    if (!detail || !activation || !activation.account || !activation.account.exists) return;
    const acc = activation.account;
    const ok = await askConfirm({
      title: "Generar nueva contraseña",
      text: `Vas a generar una contraseña nueva para ${acc.mail} (la anterior dejará de funcionar). Se la pasás vos en la respuesta. ¿Continuar?`,
      confirmLabel: "Generar contraseña",
    });
    if (!ok) return;
    setActivationBusy(true);
    postJson("/admin/inbox/activation/reset-password", { student_id: acc.student_id, email: acc.mail }).then((res) => {
      setActivationBusy(false);
      if (res.result) {
        showToast("ok", `Contraseña nueva: ${res.password}`);
        setActivationDone({ mode: "password", password: res.password, mail: acc.mail, student_id: acc.student_id });
        if (res.suggested_reply) applyAndSaveSuggestion(res.suggested_reply);
      } else showToast("err", res.error || "No se pudo generar la contraseña");
    });
  };

  // Cargar en el composer un pedido de datos según el país (AR: correo MP; resto: últimos 4 / PayPal).
  const replyAskData = () => {
    const country = (activation && ((activation.hints && activation.hints.country) || (activation.promo && activation.promo.country))) || "";
    const isAR = /argentin/i.test(country);
    const text = isAR
      ? "¡Hola! ¿Cómo estás?\n\nPara encontrar tu pago y activarte la membresía necesito el correo de Mercado Pago con el que abonaste. ¿Me lo pasás?\n\n¡Gracias y abrazo grande!"
      : "¡Hola! ¿Cómo estás?\n\nPara encontrar tu pago y activarte la membresía necesito alguno de estos datos: los últimos 4 dígitos de la tarjeta con la que pagaste, o el correo de PayPal que usaste. ¿Me lo pasás?\n\n¡Gracias y abrazo grande!";
    applyToEditor(text);
  };

  // Marcar/desmarcar un mensaje como Importante (bandeja ortogonal al status).
  const toggleImportant = (id, makeImportant) => {
    postJson("/admin/inbox/toggle-important", { id, important: makeImportant }).then((res) => {
      if (!res || res.result === false) { showToast("err", "No se pudo marcar"); return; }
      if (res.counts) setCounts(res.counts);
      setDetail((d) => (d && d.message && d.message.id === id)
        ? { ...d, message: { ...d.message, important: res.important } } : d);
      // Reflejar en la lista; si estoy en la bandeja Importante y lo desmarco, sale de la lista.
      setItems((prev) => prev
        .map((x) => (x.id === id ? { ...x, important: res.important } : x))
        .filter((x) => (filter.important ? x.important : true)));
      showToast("ok", res.important ? "Marcado como importante" : "Quitado de importantes");
    });
  };

  // ── Spam ─────────────────────────────────────────────────
  // Remitente de un mensaje, tal como lo va a bloquear el back (ver
  // InboxSpamController::addressOf: gana el external_id porque es el estable).
  const senderOf = (m) => (m && (m.author_external_id || m.author_handle)) || "";
  const domainOf = (m) => {
    const s = String(senderOf(m) || "");
    const at = s.lastIndexOf("@");
    return at > 0 ? s.slice(at + 1).toLowerCase() : "";
  };

  // Se traen todas y se filtran en el navegador: son pocas reglas y así el buscador
  // no dispara un request por tecla.
  const fetchSpamRules = useCallback(() => {
    setSpamBusy(true);
    postJson("/admin/inbox/spam/rules", {}).then((res) => {
      setSpamBusy(false);
      if (res.result) setSpamRules(res.data || []);
      else showToast("err", "No se pudieron cargar las reglas de spam");
    });
  }, []);
  useEffect(() => { if (spamOpen) fetchSpamRules(); }, [spamOpen, fetchSpamRules]);

  // Confirma y marca. scope: 'address' (este remitente) | 'domain' (todo el dominio).
  const doMarkSpam = (m, scope, reason) => {
    setBusy(true);
    postJson("/admin/inbox/spam/mark", { id: m.id, scope, reason: reason || "" }).then((res) => {
      setBusy(false);
      if (!res || res.result === false) {
        showToast("err", (res && res.error) || "No se pudo marcar como spam");
        return;
      }
      setSpamDlg(null);
      const n = res.affected || 0;
      showToast("ok", `A Spam: ${n} mensaje${n === 1 ? "" : "s"}. Lo que llegue de ${res.rule.value} entra directo ahí.`);
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      setSelectedId(null);
      fetchList();
    });
  };

  // Rescatar un mensaje suelto sin tocar la regla. Si la regla sigue activa el
  // próximo mensaje vuelve a caer en Spam, así que ofrecemos borrarla también.
  const doUnmarkSpam = (m) => {
    setBusy(true);
    postJson("/admin/inbox/spam/unmark", { id: m.id }).then(async (res) => {
      setBusy(false);
      if (!res || res.result === false) { showToast("err", "No se pudo sacar de spam"); return; }
      showToast("ok", "Vuelve a Pendientes");
      setItems((prev) => prev.filter((x) => x.id !== m.id));
      setSelectedId(null);
      fetchList();
      if (res.rule) {
        const ok = await askConfirm({
          title: "¿Desbloquear también al remitente?",
          text: `Este mensaje ya volvió a Pendientes, pero la regla sobre ${res.rule.value} sigue activa: el próximo mensaje va a caer en Spam otra vez. ¿Borrar la regla y restaurar todo lo suyo?`,
          confirmLabel: "Borrar la regla",
        });
        if (ok) deleteSpamRule(res.rule, true);
      }
    });
  };

  const toggleSpamRule = (rule) => {
    postJson("/admin/inbox/spam/rules/toggle", { id: rule.id, is_active: rule.is_active ? 0 : 1 }).then((res) => {
      if (!res || res.result === false) { showToast("err", "No se pudo cambiar la regla"); return; }
      setSpamRules((prev) => prev.map((r) => (r.id === rule.id ? res.rule : r)));
      showToast("ok", res.rule.is_active
        ? "Regla activa de nuevo"
        : "Regla pausada: lo nuevo de este remitente vuelve a Pendientes");
      fetchList();
    });
  };

  const deleteSpamRule = async (rule, skipConfirm) => {
    const n = rule.messages_in_spam;
    if (!skipConfirm) {
      const ok = await askConfirm({
        title: "Borrar la regla",
        text: `Se desbloquea ${rule.value}${n ? ` y sus ${n} mensaje${n === 1 ? "" : "s"} vuelven a Pendientes` : ""}. Lo que mande de ahora en más entra normal.`,
        confirmLabel: "Borrar y restaurar",
      });
      if (!ok) return;
    }
    postJson("/admin/inbox/spam/rules/delete", { id: rule.id, restore: 1 }).then((res) => {
      if (!res || res.result === false) { showToast("err", "No se pudo borrar la regla"); return; }
      setSpamRules((prev) => prev.filter((r) => r.id !== rule.id));
      const r = res.restored || 0;
      showToast("ok", r ? `Desbloqueado · ${r} mensaje${r === 1 ? "" : "s"} de vuelta en Pendientes` : "Desbloqueado");
      fetchList();
    });
  };

  // ── Base de conocimiento ─────────────────────────────────
  const fetchKb = useCallback(() => {
    postJson("/admin/inbox/kb/list", {}).then((res) => {
      if (res.result) setKbItems(res.data || []);
    });
  }, []);
  useEffect(() => { if (kbOpen) fetchKb(); }, [kbOpen, fetchKb]);

  // Guardar como ejemplo desde la conversación abierta (mensaje del cliente + tu respuesta).
  const saveAsExample = () => {
    if (!detail || !editedReply.trim()) return;
    const m = detail.message;
    setBusy(true);
    postJson("/admin/inbox/kb/store", {
      type: "example",
      mailbox: m.mailbox || "all",
      channel: m.channel || "all",
      customer_message: m.content || "",
      ideal_reply: editedReply,
      source_message_id: m.id,
    }).then((res) => {
      setBusy(false);
      if (res.result) showToast("ok", "Guardado en la base de conocimiento. La IA lo usará en próximas respuestas.");
      else showToast("err", res.error || "No se pudo guardar");
    });
  };

  const submitKbForm = () => {
    if (kbForm.type === "example" && !kbForm.customer_message.trim() && !kbForm.ideal_reply.trim()) {
      showToast("err", "Cargá el mensaje del cliente o la respuesta ideal."); return;
    }
    if ((kbForm.type === "guideline" || kbForm.type === "template") && !kbForm.content.trim()) {
      showToast("err", kbForm.type === "template" ? "Cargá el contenido de la plantilla." : "Cargá el contenido de la pauta."); return;
    }
    setKbBusy(true);
    postJson("/admin/inbox/kb/store", { ...kbForm, is_active: kbForm.is_active ? 1 : 0 }).then((res) => {
      setKbBusy(false);
      if (res.result) { setKbForm(emptyKbForm); setKbFormOpen(false); fetchKb(); fetchTemplates(); showToast("ok", kbForm.id ? "Entrada actualizada." : "Entrada creada."); }
      else showToast("err", res.error || "No se pudo guardar");
    });
  };

  const editKb = (e) => {
    setKbForm({
      id: e.id, title: e.title || "", type: e.type, mailbox: e.mailbox || "all", channel: e.channel || "all",
      customer_message: e.customer_message || "", ideal_reply: e.ideal_reply || "",
      content: e.content || "", tags: e.tags || "", priority: e.priority || 0, is_active: !!e.is_active,
    });
    setKbFormOpen(true);
  };

  // Plantillas para insertar en el composer (se cargan al montar y tras guardar).
  const fetchTemplates = useCallback(() => {
    postJson("/admin/inbox/kb/list", { type: "template" }).then((res) => {
      if (res.result) setTemplates((res.data || []).filter((t) => t.is_active));
    });
  }, []);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => {
    if (!templatesOpen) return;
    const onDoc = (e) => { if (templatesRef.current && !templatesRef.current.contains(e.target)) setTemplatesOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [templatesOpen]);

  const doInsertTemplate = (t) => {
    const prev = editorRef.current ? editorRef.current.innerText : editedReply;
    const next = (prev && prev.trim() ? prev.replace(/\s+$/, "") + "\n\n" + t.content : t.content);
    applyToEditor(next);
    setTemplatesOpen(false);
    showToast("ok", `Plantilla "${t.title}" insertada — editá y enviá.`);
  };

  // Copiar el email del contacto al portapapeles.
  const copyEmail = (addr) => {
    if (!addr) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(addr)
        .then(() => showToast("ok", `Copiado: ${addr}`))
        .catch(() => showToast("err", "No se pudo copiar"));
    } else {
      // Fallback para contextos sin Clipboard API.
      const ta = document.createElement("textarea");
      ta.value = addr; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); showToast("ok", `Copiado: ${addr}`); }
      catch (e) { showToast("err", "No se pudo copiar"); }
      document.body.removeChild(ta);
    }
  };

  // Mejorar/emprolijar el borrador en la voz de Mario (con validación humana).
  const doImprove = (instruction) => {
    if (!detail || !editedReply.trim()) return;
    setBusy(true); setBusyAction("improve");
    setImproveOpen(false);
    postJson("/admin/inbox/improve-message", { id: detail.message.id, text: editedReply, instruction: (instruction || "").trim() }).then((res) => {
      setBusy(false);
      if (res.result && res.text) { applyToEditor(res.text); setImproveHint(""); showToast("ok", "Mensaje mejorado — revisalo antes de enviar."); }
      else showToast("err", res.error || "No se pudo mejorar el mensaje");
    }).catch(() => { setBusy(false); showToast("err", "Error al mejorar el mensaje"); });
  };

  const toggleKb = (id) => postJson("/admin/inbox/kb/toggle", { id }).then((res) => { if (res.result) fetchKb(); });

  const deleteKb = async (id) => {
    const ok = await askConfirm({ title: "Borrar entrada", text: "Se elimina de la base de conocimiento. No afecta los mensajes.", confirmLabel: "Borrar", danger: true });
    if (!ok) return;
    postJson("/admin/inbox/kb/delete", { id }).then((res) => {
      if (res.result) { fetchKb(); showToast("ok", "Entrada borrada."); }
      else showToast("err", res.error || "No se pudo borrar");
    });
  };

  // Group thread messages by day for separators
  const threadGrouped = useMemo(() => {
    if (!detail) return [];
    const groups = []; let lastDay = null;
    detail.thread_messages.forEach((m) => {
      const day = new Date(m.received_at).toDateString();
      if (day !== lastDay) {
        groups.push({ type: "sep", date: m.received_at, key: "sep-" + m.id });
        lastDay = day;
      }
      groups.push({ type: "msg", msg: m, key: m.id });
    });
    return groups;
  }, [detail]);

  // Al abrir un hilo, posicionar sobre el ÚLTIMO mensaje entrante del contacto
  // (no arriba de todo). Marcamos ese mensaje y lo scrolleamos a la vista.
  const lastIncomingRef = useRef(null);
  const lastIncomingId = useMemo(() => {
    if (!detail || !Array.isArray(detail.thread_messages)) return null;
    let id = null;
    detail.thread_messages.forEach((mm) => { if (mm.direction === "incoming") id = mm.id; });
    return id;
  }, [detail]);
  const detailMsgId = detail && detail.message ? detail.message.id : null;
  useEffect(() => {
    const el = lastIncomingRef.current;
    if (!el) return;
    // pequeño delay para que el layout (incl. thumbnails) asiente antes de scrollear
    const t = setTimeout(() => { try { el.scrollIntoView({ block: "center" }); } catch (e) {} }, 60);
    return () => clearTimeout(t);
  }, [detailMsgId]);

  // ── Sidebar config ───────────────────────────────────────
  const channelKeys = ["all", "whatsapp", "meta_fb", "meta_ig", "youtube", "email"];
  const statuses = [
    { v: "pending", l: "Pendientes", icon: "inbox" },
    { v: "replied", l: "Respondidos", icon: "check" },
    { v: "ignored", l: "Ignorados", icon: "archive" },
  ];

  const activeStatusLabel = filter.important
    ? "Importante"
    : (filter.status === "spam" ? "Spam" : (statuses.find(s => s.v === filter.status)?.l || "Todos"));

  // Filtrado del buscador de reglas de spam (client-side: son pocas).
  const shownSpamRules = (() => {
    const q = spamSearch.trim().toLowerCase();
    if (!q) return spamRules;
    return spamRules.filter((r) =>
      [r.value, r.label, r.reason].some((f) => String(f || "").toLowerCase().includes(q)));
  })();

  // Badge según el modo de la fila:
  //  - direct (WhatsApp/Email): todo lo pendiente.
  //  - dm (FB/IG en Directos): DMs pendientes.
  //  - comment (FB/IG/YT en Comentarios): solo agresivos.
  const shownBy = {
    direct:  (c) => (counts.by_channel && counts.by_channel[c]) || 0,
    dm:      (c) => (counts.by_channel_dm && counts.by_channel_dm[c]) || 0,
    comment: (c) => (counts.by_channel_hostile && counts.by_channel_hostile[c]) || 0,
  };

  // Pendientes = WhatsApp + Email por buzón + FB/IG DMs + comentarios agresivos.
  const mailboxPending = Object.keys(MAILBOX)
    .reduce((s, mb) => s + ((counts.by_mailbox && counts.by_mailbox[mb]) || 0), 0);
  const filteredPending =
    DIRECT_CHANNELS.reduce((s, c) => s + shownBy.direct(c), 0)
    + mailboxPending
    + DM_CHANNELS.reduce((s, c) => s + shownBy.dm(c), 0)
    + POST_CHANNELS.reduce((s, c) => s + shownBy.comment(c), 0);

  // Fila de canal en el sidebar. mode ∈ {direct, dm, comment}.
  // FB/IG aparecen dos veces (dm en Directos, comment en Comentarios) → key con mode.
  const renderChannelRow = (c, mode) => {
    const kind = mode === "dm" ? "dm" : mode === "comment" ? "comment" : "all";
    const active = filter.channel === c && filter.kind === kind;
    const isComment = mode === "comment";
    const badgeCount = shownBy[mode](c);
    const syncKey = mode === "dm" ? `${c}_dm` : c;
    const lsIso = counts.last_sync ? counts.last_sync[syncKey] : null;
    const lsTip = lsIso ? `Última sync: ${fmtAgo(new Date(lsIso))}`
      : (isComment ? "Comentarios (vía webhook)" : "Sin sincronizar aún");
    const title = isComment ? `${lsTip} · badge = comentarios agresivos`
      : (mode === "dm" ? `${lsTip} · mensajes directos` : lsTip);
    const ignoreScope = mode === "dm" ? { channel: c, kind: "dm" }
      : isComment ? { channel: c, kind: "comment", aggressive: 1 }
      : { channel: c };
    return (
      <div key={`${c}-${mode}`} className={classes.navRow}>
        <button
          title={title}
          style={{ flex: 1, minWidth: 0 }}
          className={`${classes.navBtn} ${active ? classes.navBtnActive : ""}`}
          onClick={() => { setFilter({ ...filter, channel: c, kind, mailbox: "all", postAgg: true, important: false }); setSelectedId(null); }}>
          <span className={classes.channelChip} style={{ background: CHANNEL[c].color }}>{CHANNEL[c].short}</span>
          <span className={classes.navBtnLabel}>{CHANNEL[c].label}</span>
          {badgeCount > 0 && (
            <span className={`${classes.navBadge} ${isComment ? classes.navBadgeAlert : ""} ${active ? classes.navBadgeActive : ""}`}>{badgeCount}</span>
          )}
        </button>
        {badgeCount > 0 && (
          <button
            className={classes.ignoreAllBtn}
            title={isComment ? "Ignorar todos los agresivos" : "Ignorar todos"}
            onClick={(e) => { e.stopPropagation(); doIgnoreAll(ignoreScope, CHANNEL[c].label); }}>
            <Icon name="archive" size={14} />
          </button>
        )}
      </div>
    );
  };

  // Fila de email por buzón (Email Premium / Email Soporte) — siempre visible.
  const renderMailboxRow = (mb) => {
    const info = MAILBOX[mb];
    if (!info) return null;
    const active = filter.channel === "email" && filter.mailbox === mb;
    const badgeCount = (counts.by_mailbox && counts.by_mailbox[mb]) || 0;
    const lsIso = counts.last_sync ? counts.last_sync.email : null;
    return (
      <div key={`email-${mb}`} className={classes.navRow}>
        <button
          title={info.addr + (lsIso ? ` · última sync ${fmtAgo(new Date(lsIso))}` : "")}
          style={{ flex: 1, minWidth: 0 }}
          className={`${classes.navBtn} ${active ? classes.navBtnActive : ""}`}
          onClick={() => { setFilter({ ...filter, channel: "email", mailbox: mb, kind: "all", postAgg: true, important: false }); setSelectedId(null); }}>
          <span className={classes.channelChip} style={{ background: info.dot, color: "#1f1d1a" }}>@</span>
          <span className={classes.navBtnLabel}>Email {info.label}</span>
          {badgeCount > 0 && (
            <span className={`${classes.navBadge} ${active ? classes.navBadgeActive : ""}`}>{badgeCount}</span>
          )}
        </button>
        {badgeCount > 0 && (
          <button className={classes.ignoreAllBtn} title="Ignorar todos"
            onClick={(e) => { e.stopPropagation(); doIgnoreAll({ channel: "email", mailbox: mb }, `Email ${info.label}`); }}>
            <Icon name="archive" size={14} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      {/* ── Top bar ───────────────────────────────────── */}
      <div className={classes.topbar}>
        <button
          className={classes.burger}
          onClick={() => setNavOpen(true)}
          aria-label="Abrir bandejas"
          title="Bandejas"
        >
          <Icon name="menu" size={18} />
        </button>

        <div className={classes.brand}>
          <div className={classes.brandIcon}>
            <Icon name="inbox" size={18} color="#fff" />
          </div>
          Centro de Mensajes
        </div>

        <div className={classes.topSearch}>
          <span className={classes.topSearchIcon}><Icon name="search" size={15} /></span>
          <input
            className={classes.topSearchInput}
            placeholder="Buscar por contenido o remitente…"
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>

        {syncMsg && <span className={classes.syncMeta} title={syncMsg}>{syncMsg}</span>}
        {!syncMsg && (() => {
          // El "Última sync" del topbar refleja el más viejo entre los canales
          // (porque si UNO de ellos está atrasado conviene saberlo). Tooltip muestra el detalle por canal.
          const ls = counts.last_sync || {};
          const entries = Object.entries(ls);
          if (!entries.length && !lastSync) return null;
          const ts = entries.map(([, v]) => new Date(v).getTime()).filter((n) => !isNaN(n));
          const oldest = ts.length ? Math.min(...ts) : (lastSync ? lastSync.getTime() : Date.now());
          const tooltip = entries
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([ch, iso]) => `${CHANNEL[ch] ? CHANNEL[ch].label : ch}: ${fmtAgo(new Date(iso))}`)
            .join("\n");
          return (
            <span className={classes.syncMeta} title={tooltip || ""}>
              Última sync: {fmtAgo(new Date(oldest))}
            </span>
          );
        })()}

        <button className={classes.topAction} onClick={() => { setPayOpen(true); setPaySearch(""); setPayResults([]); setPayTotal(null); }} title="Buscar pagos (mail, nombre, últimos 4, nº de operación)">
          <Icon name="credit_card" size={14} /> <span className={classes.topActionLabel}>Pagos</span>
        </button>

        <button className={classes.topAction} onClick={() => { setKbForm(emptyKbForm); setKbOpen(true); }} title="Base de conocimiento: ejemplos y pautas que usa la IA">
          <Icon name="book" size={14} /> <span className={classes.topActionLabel}>Base de conocimiento</span>
        </button>

        <button className={classes.topAction} disabled={syncing} onClick={doSync} title="Sincronizar Email y YouTube">
          <span className={syncing ? classes.spinning : ""}><Icon name="refresh" size={14} /></span>
          <span className={classes.topActionLabel}>{syncing ? "Sincronizando…" : "Sincronizar"}</span>
        </button>
      </div>

      {/* ── Body ───────────────────────────────────────── */}
      <div className={`${classes.body} ${selectedId ? classes.bodyPicked : ""}`}>
        {navOpen && (
          <div className={classes.navScrim} onClick={() => setNavOpen(false)} />
        )}
        {/* Sidebar */}
        <div
          className={`${classes.sidebar} ${navOpen ? classes.sidebarOpen : ""}`}
          onClick={() => setNavOpen(false)}
        >
          <div className={classes.sectionLabel}>Bandeja</div>
          {statuses.map((s) => {
            const active = !filter.important && filter.status === s.v;
            return (
              <button key={s.v}
                className={`${classes.navBtn} ${active ? classes.navBtnActive : ""}`}
                onClick={() => { setFilter({ ...filter, status: s.v, channel: "all", mailbox: "all", kind: "all", important: false }); setSelectedId(null); }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Icon name={s.icon} size={13} color={active ? "#e9b163" : "#94A3B8"} />
                  <span className={classes.navBtnLabel}>{s.l}</span>
                </span>
                {s.v === "pending" && filteredPending > 0 && (
                  <span className={`${classes.navBadge} ${active ? classes.navBadgeActive : ""}`}>{filteredPending}</span>
                )}
              </button>
            );
          })}
          {/* Importante: bandeja ortogonal al status — el mensaje queda acá aunque ya
              se haya respondido, para no perderle el rastro. */}
          <button
            className={`${classes.navBtn} ${filter.important ? classes.navBtnActive : ""}`}
            onClick={() => { setFilter({ ...filter, important: true }); setSelectedId(null); }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Icon name="star" size={13} color={filter.important ? "#e9b163" : "#F59E0B"} />
              <span className={classes.navBtnLabel}>Importante</span>
            </span>
            {counts.important > 0 && (
              <span className={`${classes.navBadge} ${filter.important ? classes.navBadgeActive : ""}`}>{counts.important}</span>
            )}
          </button>

          {/* Spam: remitentes bloqueados desde el panel. El engranaje abre la lista de
              reglas para revisar y revertir cualquier caso. */}
          {(() => {
            const active = !filter.important && filter.status === "spam";
            return (
              <div className={classes.navRow}>
                <button
                  style={{ flex: 1, minWidth: 0 }}
                  title="Mensajes de remitentes bloqueados. No afecta a la casilla de correo real."
                  className={`${classes.navBtn} ${active ? classes.navBtnActive : ""}`}
                  onClick={() => { setFilter({ ...filter, status: "spam", channel: "all", mailbox: "all", kind: "all", important: false }); setSelectedId(null); }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Icon name="shield" size={13} color={active ? "#e9b163" : "#94A3B8"} />
                    <span className={classes.navBtnLabel}>Spam</span>
                  </span>
                  {counts.spam > 0 && (
                    <span className={`${classes.navBadge} ${active ? classes.navBadgeActive : ""}`}>{counts.spam}</span>
                  )}
                </button>
                <button className={classes.ignoreAllBtn} title="Remitentes bloqueados: ver y revertir"
                  onClick={(e) => { e.stopPropagation(); setSpamOpen(true); }}>
                  <Icon name="settings" size={14} />
                </button>
              </div>
            );
          })()}

          <div className={classes.sectionLabel}>Mensajes directos</div>
          {DIRECT_CHANNELS.map((c) => renderChannelRow(c, "direct"))}
          {Object.keys(MAILBOX).map((mb) => renderMailboxRow(mb))}
          {DM_CHANNELS.map((c) => renderChannelRow(c, "dm"))}

          <div className={classes.sectionLabel}>Comentarios</div>
          {POST_CHANNELS.map((c) => renderChannelRow(c, "comment"))}
          {(() => {
            // Toggle siempre visible; solo aplica cuando se ven comentarios.
            // En directos/DMs queda atenuado/inerte (no esconde mails, WhatsApp ni DMs).
            const onPost = filter.kind === "comment";
            return (
              <div
                className={classes.aggToggle}
                style={onPost ? undefined : { opacity: 0.45 }}
                title={onPost ? "Filtra los comentarios agresivos del post" : "El filtro de agresivos solo aplica a posts (FB/IG/YT)"}>
                <button disabled={!onPost}
                  className={`${classes.aggToggleBtn} ${filter.postAgg ? classes.aggToggleBtnActive : ""}`}
                  onClick={() => setFilter({ ...filter, postAgg: true })}>Solo agresivos</button>
                <button disabled={!onPost}
                  className={`${classes.aggToggleBtn} ${!filter.postAgg ? classes.aggToggleBtnActive : ""}`}
                  onClick={() => setFilter({ ...filter, postAgg: false })}>Todos</button>
              </div>
            );
          })()}

        </div>

        {/* List */}
        <div className={classes.list}>
          <div className={classes.listHead}>
            <div className={classes.listHeadTitle}>{activeStatusLabel}</div>
            <div className={classes.listHeadCount}>
              {items.length} {items.length === 1 ? "conversación" : "conversaciones"}
            </div>
          </div>
          <div className={classes.listScroll} ref={listRef}>
            {items.length === 0 && (
              <div className={classes.empty} style={{ padding: 32 }}>
                <div className={classes.emptyIcon}><Icon name="inbox" size={28} /></div>
                <div className={classes.emptyTitle}>Sin mensajes</div>
                <div className={classes.emptyText}>No hay conversaciones que coincidan con los filtros actuales.</div>
              </div>
            )}
            {items.map((m) => {
              const ch = CHANNEL[m.channel] || CHANNEL.email;
              const author = decodeMimeHeader(m.author_name) || m.author_handle || "—";
              const isDm = m.thread && m.thread.parent_ref && m.thread.parent_ref.kind === "dm";
              return (
                <div key={m.id}
                  data-msg-id={m.id}
                  className={`${classes.msgItem} ${selectedId === m.id ? classes.msgItemActive : ""}`}
                  onClick={() => { if (selectedId === m.id) fetchDetail(m.id); else setSelectedId(m.id); }}>
                  <div className={classes.avatar} style={{ background: avatarColor(author) }}>
                    {initials(author)}
                    <span className={classes.avatarChannel} style={{ background: ch.color }}>{ch.short}</span>
                  </div>
                  <div className={classes.msgBody}>
                    <div className={classes.msgRow1}>
                      <span className={classes.msgAuthor}>{author}</span>
                      {m.important && (
                        <span title="Importante" style={{ marginLeft: 5, display: "inline-flex" }}>
                          <Icon name="star" size={12} color="#F59E0B" />
                        </span>
                      )}
                      {isDm && (
                        <span
                          title="Mensaje directo"
                          style={{
                            background: "#DBEAFE", color: "#1E40AF",
                            fontSize: 10, fontWeight: 700, padding: "1px 6px",
                            borderRadius: 4, marginLeft: 6, letterSpacing: 0.3,
                          }}>
                          DM
                        </span>
                      )}
                      {m.dup_count > 1 && (
                        <span
                          title={`${m.dup_count} mensajes de este autor en el mismo hilo`}
                          style={{
                            background: "#E0E7FF", color: "#4338CA",
                            fontSize: 10, fontWeight: 600, padding: "1px 6px",
                            borderRadius: 999, marginLeft: 6,
                          }}>
                          +{m.dup_count - 1}
                        </span>
                      )}
                      <span className={classes.msgTime}>{fmtTime(m.received_at)}</span>
                    </div>
                    <div className={classes.msgPreview}>{(m.content && m.content.trim())
                      ? plainPreview(m.content, m.channel)
                      : (Array.isArray(m.media) && m.media.length
                          ? (String((m.media[0] || {}).mime || "").indexOf("image/") === 0 ? "📷 Imagen" : "📎 Adjunto")
                          : plainPreview(m.content, m.channel))}</div>
                    <div className={classes.msgTags}>
                      {m.channel === "email" && m.mailbox && MAILBOX[m.mailbox] && (
                        <span
                          className={classes.pill}
                          style={{ background: MAILBOX[m.mailbox].bg, color: MAILBOX[m.mailbox].fg }}
                          title={`Buzón: ${MAILBOX[m.mailbox].addr}`}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: MAILBOX[m.mailbox].dot, marginRight: 6 }} />
                          {MAILBOX[m.mailbox].label}
                        </span>
                      )}
                      {customerCountry(m) && (
                        <span className={classes.pill} style={{ background: "#ECFEFF", color: "#155E75" }} title="País del cliente">
                          {countryFlag(customerCountry(m)) ? <span style={{ fontSize: 11, lineHeight: 1 }}>{countryFlag(customerCountry(m))}</span> : <Icon name="globe" size={10} />} {customerCountry(m)}
                        </span>
                      )}
                      {Array.isArray(m.media) && m.media.length > 0 && (
                        <span className={classes.pill} style={{ background: "#F1F5F9", color: "#475569" }} title="Tiene adjuntos (comprobantes)">
                          <Icon name="paperclip" size={10} /> {m.media.length}
                        </span>
                      )}
                      {m.ai_sentiment && SENTIMENT[m.ai_sentiment] && (
                        <SentimentBar sentiment={m.ai_sentiment} score={m.ai_sentiment_score} />
                      )}
                      {(() => {
                        const lvl = hostilityLevel(m.ai_hostility, m.ai_hostility_target);
                        if (!lvl || lvl === "none") return null;
                        const h = HOSTILITY[lvl];
                        const tgt = HOSTILITY_TARGET[m.ai_hostility_target] || "";
                        return (
                          <span
                            className={classes.pill}
                            style={{ background: h.bg, color: h.fg }}
                            title={`Agresión ${tgt ? "a " + tgt : ""} · score ${Math.round((m.ai_hostility || 0) * 100)}%`}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: h.dot, marginRight: 6 }} />
                            {h.label}{tgt ? ` · ${tgt}` : ""}
                          </span>
                        );
                      })()}
                      {m.ai_sales_intent !== null && m.ai_sales_intent >= 0.5 && (
                        <span className={classes.pill} style={{ background: "#FEF3C7", color: "#92400E" }}>
                          <Icon name="star" size={10} />
                          {Math.round(m.ai_sales_intent * 100)}% venta
                        </span>
                      )}
                      {m.status === "pending" && (
                        <button
                          onClick={(e) => quickIgnore(m.id, e)}
                          title="Ignorar y sacar de la bandeja"
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: "transparent", color: "#475569",
                            border: "1px solid #CBD5E1", borderRadius: 999,
                            padding: "2px 8px", fontSize: 11, cursor: "pointer", lineHeight: 1.2,
                          }}>
                          <Icon name="archive" size={11} /> Ignorar
                        </button>
                      )}
                      {m.status === "spam" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); doUnmarkSpam(m); }}
                          title="Devolver este mensaje a Pendientes"
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: "transparent", color: "#475569",
                            border: "1px solid #CBD5E1", borderRadius: 999,
                            padding: "2px 8px", fontSize: 11, cursor: "pointer", lineHeight: 1.2,
                          }}>
                          <Icon name="undo" size={11} /> No es spam
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        {!detail && (
          <div className={classes.empty}>
            <div className={classes.emptyIcon}><Icon name="inbox" size={32} /></div>
            <div className={classes.emptyTitle}>Seleccioná una conversación</div>
            <div className={classes.emptyText}>Elegí un mensaje de la lista para ver el hilo completo y responder.</div>
          </div>
        )}
        {detail && (() => {
          const m = detail.message;
          const ch = CHANNEL[m.channel] || CHANNEL.email;
          const author = decodeMimeHeader(m.author_name) || m.author_handle || "—";
          const status = STATUS_PILL[m.status];
          // País del que escribe → tag en la cabecera. PRIORIDAD: la cuenta/pago detectados
          // (dato firme de la DB) mandan sobre el país del formulario de contacto o lo que
          // adivine la IA del texto — esos dos suelen errarle (p. ej. toman "Argentina" del
          // pie de TCDG y tapan el país real del alumno).
          const _acc = manualAccount || (activation && activation.account) || {};
          // País ÚNICO: si elegiste un alumno a mano, el suyo; si no, el que resolvió el backend
          // (`activation.country`) — el MISMO que usa el precio/promo, así nunca difiere el tag
          // del precio. Recién al final caemos al formulario de contacto.
          const detectedCountry = _acc.country
            || (activation && activation.country)
            || customerCountry(m)
            || null;
          // ¿Mostramos el asistente de activación? En email, siempre (está a mano). En WhatsApp/DM,
          // solo cuando hay algo que activar (pedido de activación, comprobante, pago matcheado,
          // cuenta detectada o alumno elegido a mano) para no ensuciar cada chat trivial.
          const isDm = detail.thread && detail.thread.parent_ref && detail.thread.parent_ref.kind === "dm";
          const activationChannel = m.channel === "email" || m.channel === "whatsapp" || isDm;
          const activationRelevant = m.channel === "email" || !!manualAccount || !!(activation && (
            activation.activation_request
            || (Array.isArray(activation.receipts) && activation.receipts.length)
            || (Array.isArray(activation.matched_payments) && activation.matched_payments.length)
            || (activation.account && activation.account.exists)
          ));
          const showActivationPanel = activationChannel && activationRelevant;
          return (
            <div className={classes.detail}>
              <div className={classes.detailHead}>
                <button
                  className={classes.backBtn}
                  onClick={() => setSelectedId(null)}
                  aria-label="Volver a la lista"
                  title="Volver"
                >
                  <Icon name="back" size={17} />
                </button>
                <div className={classes.detailAvatarBig} style={{ background: avatarColor(author) }}>
                  {initials(author)}
                </div>
                <div className={classes.detailMain}>
                  <div className={classes.detailAuthor}>
                    {author}
                    {detectedCountry && (
                      <span
                        title="País del cliente (de la cuenta/pago detectados o, si no, del mensaje)"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          background: "#CFFAFE", color: "#155E75", border: "1px solid #67E8F9",
                          fontSize: 13, fontWeight: 800, padding: "3px 12px", borderRadius: 999,
                          marginLeft: 12, verticalAlign: "middle",
                        }}>
                        {countryFlag(detectedCountry) ? <span style={{ fontSize: 14, lineHeight: 1 }}>{countryFlag(detectedCountry)}</span> : <Icon name="globe" size={14} />} {detectedCountry}
                      </span>
                    )}
                  </div>
                  <div className={classes.detailMeta}>
                    <span className={classes.channelChip} style={{ background: ch.color, marginRight: 6, verticalAlign: "middle" }}>{ch.short}</span>
                    {ch.label}
                    {m.channel === "email" && m.mailbox && MAILBOX[m.mailbox] && (
                      <span
                        title={`Las respuestas saldrán desde ${MAILBOX[m.mailbox].addr}`}
                        style={{
                          background: MAILBOX[m.mailbox].bg, color: MAILBOX[m.mailbox].fg,
                          fontSize: 10, fontWeight: 700, padding: "1px 7px",
                          borderRadius: 4, marginLeft: 8, letterSpacing: 0.2, verticalAlign: "middle",
                        }}>
                        {MAILBOX[m.mailbox].label}
                      </span>
                    )}
                    {detail.thread && detail.thread.parent_ref && detail.thread.parent_ref.kind === "dm" ? (
                      <span style={{
                        background: "#DBEAFE", color: "#1E40AF",
                        fontSize: 10, fontWeight: 700, padding: "1px 6px",
                        borderRadius: 4, marginLeft: 8, letterSpacing: 0.3,
                        verticalAlign: "middle",
                      }}>MENSAJE DIRECTO</span>
                    ) : (
                      detail.thread && detail.thread.parent_ref && detail.thread.parent_ref.title &&
                        <> · sobre "{detail.thread.parent_ref.title}"</>
                    )}
                    {m.author_handle && m.author_handle !== author && (
                      <> · <span
                        onClick={() => copyEmail(m.author_handle)}
                        title="Clic para copiar el email"
                        style={{ color: "#64748B", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2 }}
                      >{m.author_handle}</span></>
                    )}
                    {detail.thread && detail.thread.subject && <> · {detail.thread.subject}</>}
                  </div>
                </div>
                {status && (
                  <span className={classes.pill} style={{ background: status.bg, color: status.fg }}>
                    {status.label}
                  </span>
                )}
                {detail.thread && detail.thread.parent_ref && detail.thread.parent_ref.url && (
                  <a
                    href={detail.thread.parent_ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${classes.btn} ${classes.btnGhost}`}
                    style={{ textDecoration: "none" }}
                    title={"Abrir en " + ch.label}
                  >
                    <Icon name="external" size={13} /> Ver post
                  </a>
                )}
                <button className={classes.iconBtn} onClick={() => toggleImportant(m.id, !m.important)}
                  title={m.important ? "Quitar de Importantes" : "Marcar como Importante"}>
                  <Icon name="star" size={18} color={m.important ? "#F59E0B" : "#94A3B8"} />
                </button>
                <button className={classes.iconBtn} onClick={() => setSelectedId(null)} title="Cerrar">
                  <Icon name="x" size={18} />
                </button>
              </div>

              {showActivationPanel && (() => {
                const a = activation;
                const acc = manualAccount || (a && a.account) || {};
                const proj = (a && a.projection) || {};
                const promo = (a && a.promo) || {};
                const hints = (a && a.hints) || {};
                const pays = (a && a.matched_payments) || [];
                const fmtYears = (d) => d ? `${Math.round((d / 365) * 10) / 10} año${Math.round(d / 365) === 1 ? "" : "s"}` : "—";
                const assignedToAcc = acc.exists && pays.find((p) => p.already_assigned && String(p.student_id) === String(acc.student_id));
                const T = { fontSize: 13.5, color: "#1F2937" };
                const LBL = { fontSize: 11, fontWeight: 800, color: "#4338CA", textTransform: "uppercase", letterSpacing: 0.4 };
                const PayRow = (p) => (
                  // flexWrap: en un celular la fila no entra en 390px y el botón de Renovar
                  // /Activar quedaba CORTADO fuera de la pantalla — o sea, la acción más
                  // importante del panel era inalcanzable desde el teléfono. Envolviendo,
                  // el botón baja a la línea de abajo y se puede tocar.
                  <div key={p.id} className={classes.payRow} style={{ border: p.recommended ? "1.5px solid #34D399" : "1px solid #C7D2FE", background: p.recommended ? "#F0FDF4" : "#fff" }}>
                    <span title={`#${p.id} · pagado ${p.paid_at || "—"}`} style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", whiteSpace: "nowrap", flexShrink: 0 }}>🕘 {p.paid_at || "—"}</span>
                    <span title={`#${p.id} · ${p.plan || "—"} · ${p.country} · ${p.coin} ${p.price} · ${p.pay_channel || "—"} · vigencia ${p.start}→${p.end} · ${p.status}${p.country_match ? " · país ✓" : ""}`}
                      style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 1, minWidth: 0 }}>
                      · {p.country} · {p.coin} {p.price}
                    </span>
                    {p.recommended && <span title="Recomendado" style={{ background: "#DCFCE7", color: "#166534", fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 999, flexShrink: 0 }}>★</span>}
                    {p.already_applied
                      ? <span title="Ya hay una renovación aplicada del mismo monto cerca de esta fecha — no la vuelvas a aplicar" style={{ fontSize: 11, color: "#92400E", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>ya aplicado · vence {acc.current_end || "—"}</span>
                      : (proj.new_end && !p.already_assigned && <span style={{ fontSize: 11, color: "#3730A3", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>+{fmtYears(proj.duration_days)} → {proj.new_end}</span>)}
                    <span style={{ marginLeft: "auto", flexShrink: 0 }}>
                      {p.already_assigned ? (
                        <span style={{ fontSize: 11.5, color: "#6B7280" }}>✓ {p.student_mail || "asignado"}</span>
                      ) : p.already_applied ? (
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#92400E", background: "#FEF3C7", padding: "2px 8px", borderRadius: 999 }}>✓ ya aplicado</span>
                      ) : acc.exists ? (
                        <button disabled={activationBusy} onClick={() => doRenew(p)} style={{ background: "#4338CA", color: "#fff", border: 0, borderRadius: 6, padding: "5px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Renovar</button>
                      ) : (
                        <button disabled={activationBusy} onClick={() => doActivateNew(p)} style={{ background: "#047857", color: "#fff", border: 0, borderRadius: 6, padding: "5px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Activar nuevo</button>
                      )}
                    </span>
                  </div>
                );
                const actionable = pays.filter((p) => !p.already_assigned);
                const linked = pays.filter((p) => p.already_assigned);
                const showWarn = a && pays.length === 0 && (hints.card_last4 || hints.payer_email || a.activation_request);
                const hasRight = a && ((acc.exists && acc.student_id) || pays.length > 0 || showWarn);
                return (
                  <div className={`${classes.actPanel} ${actPanelAbierto ? "" : classes.actPanelCerrado}`}>
                    {/* Barra-resumen: SÓLO en celular. El panel es útil pero enorme, y con él
                        desplegado el hilo de mensajes quedaba en 40px de alto — o sea, la
                        pantalla mostraba todo menos la conversación que hay que contestar. */}
                    <button
                      type="button"
                      className={classes.actPanelToggle}
                      onClick={() => setActPanelAbierto((v) => !v)}
                      aria-expanded={actPanelAbierto}
                    >
                      <span className={classes.actPanelResumen}>{resumenActivacion(acc, actionable, a)}</span>
                      <span className={classes.actPanelChevron}>{actPanelAbierto ? "▾" : "▸"}</span>
                    </button>
                    <div className={classes.actPanelCuerpo}>
                    {activationDone && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, fontWeight: 800, color: "#065F46", background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 8, padding: "6px 11px" }}>
                        <Icon name="check" size={14} color="#059669" />
                        {activationDone.mode === "renew" ? "Membresía renovada" : activationDone.mode === "password" ? "Contraseña nueva generada" : "Cuenta activada"}
                        {activationDone.end ? ` · vence ${activationDone.end}` : ""}
                        {activationDone.password && <span style={{ fontFamily: "monospace", background: "#D1FAE5", padding: "1px 6px", borderRadius: 4 }}>{activationDone.password}</span>}
                        <span style={{ fontWeight: 600, color: "#047857" }}>· abajo tenés la respuesta lista, revisá y enviá</span>
                      </div>
                    )}
                    {a && Array.isArray(a.receipts) && a.receipts.length > 0 && (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap", fontSize: 13, color: "#1E3A8A", background: "#EFF6FF", border: "1px solid #93C5FD", borderRadius: 8, padding: "7px 11px" }}>
                        <Icon name="paperclip" size={14} color="#2563EB" />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <b>Comprobante de pago detectado.</b>{" "}
                          {a.receipts.map((r, i) => (
                            <span key={i}>{r.summary || [r.method, r.currency, r.amount].filter(Boolean).join(" ")}{i < a.receipts.length - 1 ? " · " : ""}</span>
                          ))}
                          {" "}— usá esos datos para ubicar el pago y activar/renovar abajo.
                        </span>
                      </div>
                    )}
                    {/* 2 columnas: alumno (izquierda) · pagos (derecha) → menos altura */}
                    <div style={{ display: "flex", flexDirection: "row", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
                      {/* IZQUIERDA — Alumno */}
                      <div style={{ flex: "1 1 290px", minWidth: 240, display: "flex", flexDirection: "column", gap: 3 }}>
                        {!a ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={LBL}>Alumno</span><span style={{ ...T, color: "#6B7280" }}>buscando…</span></div>
                        ) : acc.exists ? (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={LBL}>Alumno</span>
                              {acc.name ? <b style={{ ...T, fontSize: 14.5 }}>{acc.name}</b> : null}
                              <span style={{ fontSize: 12, fontWeight: 800, padding: "2px 9px", borderRadius: 999, background: acc.active ? "#DCFCE7" : "#FEE2E2", color: acc.active ? "#166534" : "#991B1B" }}>{acc.active ? "VIGENTE" : "VENCIDA"}</span>
                              {assignedToAcc && !activationDone ? <span title="La membresía ya está aplicada a esta cuenta" style={{ fontSize: 11.5, fontWeight: 800, color: "#166534", background: "#DCFCE7", padding: "2px 8px", borderRadius: 999 }}>✓ aplicada</span> : null}
                              {manualAccount ? <span style={{ fontSize: 11.5, color: "#6B7280" }}>(a mano · <button onClick={() => setManualAccount(null)} style={{ background: "none", border: 0, padding: 0, color: "#B45309", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>quitar</button>)</span> : null}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={T}>{acc.mail}</span>
                              <span style={T}>· vence <b>{acc.current_end || "—"}</b></span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <span style={LBL}>Alumno</span>
                              <span style={{ ...T, color: "#B45309", fontWeight: 700 }}>No detectado</span>
                              <button onClick={() => { setSearchOpen(true); setSearchTouched(false); setSearchResults([]); }} style={{ background: "#4338CA", color: "#fff", fontSize: 13, fontWeight: 700, borderRadius: 6, padding: "5px 11px", border: 0, cursor: "pointer" }}>🔎 Buscar alumno</button>
                            </div>
                            {/* Correo con el que se crea la cuenta al "Activar nuevo". En WhatsApp/DM el
                                mail no viene en el handle: el operador confirma acá el que dio el cliente. */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 11.5, color: "#6B7280", fontWeight: 700 }}>Correo para la cuenta</span>
                              <input
                                type="email"
                                value={activationEmailInput}
                                onChange={(e) => setActivationEmailInput(e.target.value)}
                                placeholder="correo@ejemplo.com"
                                style={{ flex: "1 1 210px", minWidth: 180, border: "1px solid #C7D2FE", borderRadius: 6, padding: "5px 9px", fontSize: 13 }}
                              />
                            </div>
                            {Array.isArray(a.account_candidates) && a.account_candidates.length > 0 && (
                              <div style={{ marginTop: 5 }}>
                                <span style={{ fontSize: 11.5, color: "#6B7280", fontWeight: 600 }}>No lo encontré por el mail (suele pasar si responden la campaña desde otro correo). ¿Es alguno de estos? Confirmá vos:</span>
                                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 5, maxHeight: 156, overflowY: "auto" }}>
                                  {a.account_candidates.map((c) => (
                                    <button key={c.student_id} onClick={() => pickCandidate(c)} title={`Usar esta cuenta · ${c.match_by}`}
                                      style={{ textAlign: "left", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "#fff", border: "1px solid #C7D2FE", borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}>
                                      <b style={{ fontSize: 13, color: "#1F2937" }}>{c.name || "(sin nombre)"}</b>
                                      <span style={{ fontSize: 11, fontWeight: 800, padding: "1px 7px", borderRadius: 999, background: c.active ? "#DCFCE7" : "#FEE2E2", color: c.active ? "#166534" : "#991B1B" }}>{c.active ? "VIGENTE" : "VENCIDA"}</span>
                                      <span style={{ fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{c.mail}</span>
                                      {c.current_end && <span style={{ fontSize: 11.5, color: "#6B7280" }}>· vence {c.current_end}</span>}
                                      <span style={{ marginLeft: "auto", fontSize: 10.5, color: "#9CA3AF", fontStyle: "italic" }}>{c.match_by}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* DERECHA — Acciones del alumno + Pagos */}
                      {hasRight && (
                        <div style={{ flex: "1 1 340px", minWidth: 300, display: "flex", flexDirection: "column", gap: 5 }}>
                          {acc.exists && acc.student_id && (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              {promo.price != null ? <span style={{ ...T, fontWeight: 700 }}>{promo.coin} {promo.price}{promo.price_usd != null ? ` (USD ${promo.price_usd})` : ""}</span> : null}
                              <button onClick={() => openDetails(acc.student_id, (a && a.country_id) || acc.country_id)} style={{ background: "none", border: 0, padding: 0, fontSize: 13.5, fontWeight: 700, color: "#4338CA", cursor: "pointer", textDecoration: "underline" }}>ver pagos</button>
                              <a href={`#/admin/Student-new/${acc.student_id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, fontWeight: 700, color: "#4338CA", textDecoration: "none" }}>ver ficha →</a>
                              <button disabled={activationBusy} onClick={doResetPassword} style={{ background: "#fff", color: "#4338CA", border: "1px solid #A5B4FC", borderRadius: 6, padding: "4px 10px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>🔑 Nueva contraseña</button>
                            </div>
                          )}
                          {pays.length > 0 ? (
                            <>
                              {actionable.length > 0 && (() => {
                                // Con uno solo no hay nada que plegar.
                                const plegable = actionable.length > 1;
                                const abierto = !plegable || renovarAbierto;
                                // Plegado se deja ver el RECOMENDADO —el que se usa casi
                                // siempre— en vez de esconder la lista entera.
                                const visibles = abierto ? actionable : [actionable.find((p) => p.recommended) || actionable[0]];
                                return (
                                <>
                                  <span
                                    onClick={plegable ? () => setRenovarAbierto((v) => !v) : undefined}
                                    title={plegable ? (abierto ? "Plegar" : `Ver los ${actionable.length}`) : undefined}
                                    style={{ ...LBL, cursor: plegable ? "pointer" : "default", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
                                  >
                                    Pagos {acc.exists ? "para renovar" : "no vinculados"}{plegable ? ` (${actionable.length})` : ""}
                                    {plegable && (
                                      <span style={{ fontWeight: 700, textTransform: "none", letterSpacing: 0, color: "#6366F1" }}>
                                        {abierto ? "▾ plegar" : `▸ ver los ${actionable.length}`}
                                      </span>
                                    )}
                                  </span>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: abierto ? 118 : "none", overflowY: abierto ? "auto" : "visible" }}>{visibles.map(PayRow)}</div>
                                </>
                                );
                              })()}
                              {!acc.exists && linked.length > 0 && (
                                <div>
                                  <button onClick={() => setPaysOpen((o) => !o)} style={{ background: "none", border: 0, padding: 0, fontSize: 12, fontWeight: 700, color: "#4338CA", cursor: "pointer" }}>{paysOpen ? "▾" : "▸"} {linked.length} pago{linked.length > 1 ? "s" : ""} que coincide{linked.length > 1 ? "n" : ""} ya asignado{linked.length > 1 ? "s" : ""} a otra cuenta</button>
                                  {paysOpen && <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 5, maxHeight: 118, overflowY: "auto" }}>{linked.map(PayRow)}</div>}
                                </div>
                              )}
                            </>
                          ) : showWarn ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "7px 11px" }}>
                              <span style={{ fontSize: 13, color: "#92400E", fontWeight: 600 }}>No se encontró el pago con los datos provistos.</span>
                              <button onClick={replyAskData} style={{ background: "#F59E0B", color: "#1F1D1A", border: 0, borderRadius: 6, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Pedir datos ({/argentin/i.test((hints.country || promo.country || "")) ? "correo MP" : "últimos 4 / PayPal"})</button>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                    </div>
                  </div>
                );
              })()}

              <div className={classes.threadArea}>
                {threadGrouped.map((g) => {
                  if (g.type === "sep") {
                    return <div key={g.key} className={classes.daySep}>{fmtDayLabel(g.date)}</div>;
                  }
                  const tm = g.msg;
                  const isOut = tm.direction === "outgoing";
                  return (
                    <div key={g.key} ref={tm.id === lastIncomingId ? lastIncomingRef : null} className={`${classes.bubbleWrap} ${isOut ? classes.bubbleWrapOut : ""} ${classes.fadeIn}`}>
                      {!isOut && tm.author_name && (
                        <div className={classes.bubbleAuthor}>{decodeMimeHeader(tm.author_name)}</div>
                      )}
                      <div
                        className={`${classes.bubble} ${isOut ? classes.bubbleOut : classes.bubbleIn}`}
                        style={{ maxWidth: tm.channel === "email" ? "94%" : "72%" }}>
                        <MessageBody content={tm.content} isOut={isOut} channel={tm.channel} originalBody={(getRawPayload(tm) || {}).original_body} hasMedia={Array.isArray(tm.media) && tm.media.length > 0} />
                        <Attachments msg={tm} isOut={isOut} />
                        <div className={classes.bubbleTime}>{fmtBubbleTime(tm.received_at)}</div>
                      </div>
                    </div>
                  );
                })}

              {(() => {
                const lvl = hostilityLevel(m.ai_hostility, m.ai_hostility_target);
                if (!lvl || lvl === "none") return null;
                const h = HOSTILITY[lvl];
                const tgt = HOSTILITY_TARGET[m.ai_hostility_target] || "";
                return (
                  <div style={{
                    display: "flex", alignItems: "center", padding: "8px 12px",
                    background: h.bg, color: h.fg, borderRadius: 8, marginBottom: 12,
                    border: `1px solid ${h.dot}33`, fontSize: 12, fontWeight: 600,
                  }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: h.dot, marginRight: 10 }} />
                    <span style={{ flex: 1 }}>
                      {h.label}{tgt ? ` contra ${tgt}` : ""} — score {Math.round((m.ai_hostility || 0) * 100)}%
                    </span>
                  </div>
                );
              })()}

              {m.ai_reasoning && (
                <div title={m.ai_reasoning} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "4px 10px", margin: "0 0 6px",
                  fontSize: 11, color: "#6B7280", lineHeight: 1.35,
                  fontStyle: "italic",
                }}>
                  <Icon name="sparkles" size={11} color="#9CA3AF" />
                  <span style={{
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
                  }}>
                    {m.ai_reasoning}
                  </span>
                </div>
              )}

              </div>

              {(() => {
                const isAiSuggested =
                  !!m.ai_suggested_reply &&
                  editedReply.trim() === (m.ai_suggested_reply || "").trim() &&
                  m.status !== "replied";
                return (
                  <div className={classes.composer} style={isAiSuggested ? {
                    border: "1px solid #C7B6F5", background: "linear-gradient(180deg, #FAF7FF 0%, #fff 60%)",
                  } : undefined}>
                    {activationDone && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "6px 10px", marginBottom: 6,
                        background: "#DCFCE7", color: "#166534",
                        borderRadius: 6, fontSize: 12, fontWeight: 700,
                      }}>
                        <Icon name="check" size={13} color="#16A34A" />
                        {activationDone.mode === "renew" ? "Renovación hecha" : activationDone.mode === "password" ? "Contraseña generada" : "Cuenta activada"} — abajo tenés la respuesta lista, revisá y enviá.
                      </div>
                    )}
                    <div
                      onMouseDown={startEditorResize}
                      title="Arrastrá para cambiar la altura del editor (arriba = más alto, abajo = más bajo)"
                      style={{ height: 16, cursor: "ns-resize", display: "flex", alignItems: "center", justifyContent: "center", userSelect: "none" }}>
                      <div style={{ width: 44, height: 4, borderRadius: 2, background: "#CBD5E1" }} />
                    </div>
                    <div
                      ref={editorRef}
                      className={classes.composerEditor}
                      contentEditable
                      suppressContentEditableWarning
                      data-placeholder={m.ai_suggested_reply ? "Editá la sugerencia o escribí tu respuesta…" : "Escribí una respuesta… (Cmd+B negrita · Cmd+I itálica)"}
                      style={{ height: editorHeight }}
                      onInput={() => setEditedReply(editorRef.current ? editorRef.current.innerText : "")}
                      onBlur={saveSuggestion}
                      onClick={(e) => {
                        // En un editor los links no navegan al clickear; con Cmd/Ctrl+click
                        // los abrimos para poder verificarlos (en el mail enviado son clickeables).
                        const a = e.target.closest && e.target.closest("a");
                        if (a && a.href && (e.metaKey || e.ctrlKey)) { e.preventDefault(); window.open(a.href, "_blank", "noopener"); }
                      }}
                    />
                    {m.channel === "email" && detail.signature && (
                      <div style={{ marginTop: 6, fontSize: 11, color: "#94A3B8", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <Icon name="info" size={12} />
                        <span>La firma de TCDG se añade automáticamente al enviar — no hace falta que la escribas.</span>
                        <button onClick={() => setSigPreview((v) => !v)} style={{ background: "none", border: "none", color: "#4F46E5", cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" }}>{sigPreview ? "ocultar firma" : "ver firma"}</button>
                        {m.ai_classified_at && (
                          <>
                            <span style={{ color: "#CBD5E1" }}>·</span>
                            <button onClick={() => openTrace(m.id)} title="Ver qué modelo y qué datos (base de conocimiento, precios, comprobantes, hilo) se usaron para armar esta respuesta — útil para fine-tuning" style={{ background: "none", border: "none", color: "#7C3AED", cursor: "pointer", fontSize: 11, padding: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <Icon name="sparkles" size={12} color="#7C3AED" /> cómo se generó esta respuesta
                            </button>
                          </>
                        )}
                        {sigPreview && (
                          <div style={{ flexBasis: "100%", marginTop: 4, padding: "8px 10px", background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 8, color: "#64748B", whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5 }}>{detail.signature}</div>
                        )}
                      </div>
                    )}
                    {!(m.channel === "email" && detail.signature) && m.ai_classified_at && (
                      <div style={{ marginTop: 4, fontSize: 11, color: "#94A3B8" }}>
                        <button onClick={() => openTrace(m.id)} title="Ver qué modelo y qué datos se usaron para armar esta respuesta — útil para fine-tuning" style={{ background: "none", border: "none", color: "#7C3AED", cursor: "pointer", fontSize: 11, padding: 0, display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Icon name="sparkles" size={12} color="#7C3AED" /> cómo se generó esta respuesta
                        </button>
                      </div>
                    )}
                    {improveOpen && (
                      <div style={{ marginBottom: 8, padding: "10px 12px", background: "#FAF7FF", border: "1px solid #C7B6F5", borderRadius: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#5B21B6", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon name="sparkles" size={12} color="#5B21B6" /> Mejorar — indicación opcional
                        </div>
                        <textarea
                          autoFocus
                          value={improveHint}
                          onChange={(e) => setImproveHint(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); doImprove(improveHint); } }}
                          placeholder="Opcional: cómo querés que la mejore. Ej: más corta y cálida · sumá el link de pago · tuteá · más formal · sacá la promo…"
                          rows={2}
                          style={{ width: "100%", padding: "8px 10px", fontSize: 13.5, border: "1px solid #CBD5E1", borderRadius: 8, outline: "none", color: "#1F2937", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
                        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                          <button disabled={busy} onClick={() => doImprove(improveHint)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#7C3AED", color: "#fff", border: 0, borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                            {busyAction === "improve" ? <><Spinner size={12} color="#fff" /> Mejorando…</> : <><Icon name="sparkles" size={12} color="#fff" /> Mejorar</>}
                          </button>
                          <button onClick={() => setImproveOpen(false)} style={{ background: "none", border: 0, color: "#64748B", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
                          <span style={{ fontSize: 11, color: "#94A3B8", marginLeft: "auto" }}>Vacío = solo emprolija · ⌘/Ctrl+Enter aplica</span>
                        </div>
                      </div>
                    )}
                    <div className={classes.composerActions}>
                      <button className={`${classes.btn} ${classes.btnPrimary}`} disabled={busy || !editedReply.trim()} onClick={doReply}>
                        {busyAction === "reply" ? <><Spinner size={13} /> Enviando…</> : <><Icon name="send" size={13} /> Enviar</>}
                      </button>
                      <button className={`${classes.btn} ${classes.btnGhost}`} disabled={busy} onClick={() => doRegenerate("")} title="Generar la respuesta con IA on-demand (tono según la base de conocimiento)">
                        {busyAction === "regenerate" ? <><Spinner size={13} /> Generando…</> : <><Icon name="sparkles" size={13} /> Generar respuesta</>}
                      </button>
                      <button
                        className={`${classes.btn} ${classes.btnGhost}`}
                        disabled={busy || !editedReply.trim()}
                        onClick={() => setImproveOpen((o) => !o)}
                        title="Emprolijá tu borrador en la voz de Mario; podés agregar una indicación opcional">
                        <Icon name="sparkles" size={13} /> Mejorar <Icon name="chevron_down" size={14} />
                      </button>
                      {templates.length > 0 && (
                        <div className={classes.moreWrap} ref={templatesRef}>
                          <button className={`${classes.btn} ${classes.btnGhost}`} onClick={(e) => { const p = dropPos(e); setTemplatesPos(p); setTemplatesOpen((v) => !v); }} title="Insertar una plantilla de respuesta">
                            <Icon name="book" size={13} /> Plantillas <Icon name="chevron_down" size={14} />
                          </button>
                          {templatesOpen && (
                            <div className={classes.morePop} style={{ ...fixedPopStyle(templatesPos), maxHeight: 300, overflowY: "auto", minWidth: 240 }}>
                              {templates.map((t) => (
                                <button key={t.id} className={classes.morePopItem} onClick={() => doInsertTemplate(t)} title={t.content}>
                                  <Icon name="book" size={14} /> {t.title}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        className={`${classes.btn} ${classes.btnGhost}`}
                        disabled={busy || !editedReply.trim() || !m.content}
                        onClick={saveAsExample}
                        title="Guardar este mensaje + tu respuesta como ejemplo para que la IA aprenda">
                        <Icon name="book" size={13} /> Guardar como ejemplo
                      </button>
                  <div className={classes.spacer} />
                  {/* Fuera de spam: rescatar el mensaje de un solo toque, sin abrir el menú. */}
                  {m.status === "spam" && (
                    <button
                      className={`${classes.btn} ${classes.btnGhost}`}
                      disabled={busy}
                      onClick={() => doUnmarkSpam(m)}
                      title="Devolver este mensaje a Pendientes">
                      <Icon name="undo" size={13} /> No es spam
                    </button>
                  )}
                  {(ch.actions || []).length > 0 && (
                    <div className={classes.moreWrap} ref={moreRef}>
                      <button
                        className={`${classes.btn} ${classes.btnGhost}`}
                        onClick={(e) => { const p = dropPos(e); setMorePos(p); setMoreOpen((v) => !v); }}
                        title="Más acciones"
                      >
                        Más acciones
                        <Icon name="chevron_down" size={14} />
                      </button>
                      {moreOpen && (
                        <div className={classes.morePop} style={fixedPopStyle(morePos)}>
                          {m.status !== "spam" && senderOf(m) && (
                            <button
                              className={`${classes.morePopItem} ${classes.morePopItemDanger}`}
                              disabled={busy}
                              onClick={() => { setMoreOpen(false); setSpamDlg({ message: m, scope: "address", reason: "" }); }}>
                              <Icon name="shield" size={14} />
                              Marcar como spam
                            </button>
                          )}
                          {ch.actions.map((act) => {
                            const meta = ACTION_META[act];
                            if (!meta) return null;
                            return (
                              <button
                                key={act}
                                className={`${classes.morePopItem} ${meta.danger ? classes.morePopItemDanger : ""}`}
                                disabled={busy}
                                onClick={() => { setMoreOpen(false); act === "close" ? doCloseConversation() : doModerate(act); }}
                              >
                                <Icon name={meta.icon} size={14} />
                                {meta.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
                );
              })()}
            </div>
          );
        })()}
      </div>
      {kbOpen && (
        <div
          onClick={() => setKbOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9997,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex", justifyContent: "flex-end",
            backdropFilter: "blur(2px)",
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", width: 700, maxWidth: "100%",
              height: "100vh", display: "flex", flexDirection: "column",
              boxShadow: "-8px 0 30px rgba(0,0,0,0.25)",
            }}>
            {/* Header */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: "#EEF2FF", color: "#4338CA", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="book" size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Base de conocimiento</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>Ejemplos y pautas que la IA usa para sugerir respuestas. {kbItems.length} {kbItems.length === 1 ? "entrada" : "entradas"}.</div>
              </div>
              <button className={`${classes.btn} ${classes.btnPrimary}`} onClick={() => { setKbForm(emptyKbForm); setKbFormOpen(true); }}><Icon name="plus" size={13} /> Nueva entrada</button>
              <button className={classes.iconBtn} onClick={() => setKbOpen(false)} title="Cerrar"><Icon name="x" size={18} /></button>
            </div>

            <div style={{ overflowY: "auto", padding: 20, flex: 1 }}>
              {/* Form (colapsable) */}
              {kbFormOpen && (() => {
                const lbl = { fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, display: "block" };
                const inp = { width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none", background: "#F8FAFC" };
                const ta = { ...inp, minHeight: 64, resize: "vertical", lineHeight: 1.45 };
                return (
                  <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 18, background: "#FCFCFD" }}>
                    <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                      <div>
                        <label style={lbl}>Tipo</label>
                        <select style={{ ...inp, width: "auto" }} value={kbForm.type} onChange={(e) => setKbForm({ ...kbForm, type: e.target.value })}>
                          <option value="example">Ejemplo (cliente → respuesta)</option>
                          <option value="template">Plantilla (respuesta con nombre)</option>
                          <option value="guideline">Pauta / regla</option>
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Buzón</label>
                        <select style={{ ...inp, width: "auto" }} value={kbForm.mailbox} onChange={(e) => setKbForm({ ...kbForm, mailbox: e.target.value })}>
                          <option value="all">Todos</option>
                          {Object.keys(MAILBOX).map((mb) => <option key={mb} value={mb}>{MAILBOX[mb].label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={lbl}>Canal</label>
                        <select style={{ ...inp, width: "auto" }} value={kbForm.channel} onChange={(e) => setKbForm({ ...kbForm, channel: e.target.value })}>
                          <option value="all">Todos</option>
                          {Object.keys(CHANNEL).map((c) => <option key={c} value={c}>{CHANNEL[c].label}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <label style={lbl}>Prioridad</label>
                        <input type="number" style={inp} value={kbForm.priority} onChange={(e) => setKbForm({ ...kbForm, priority: Number(e.target.value) || 0 })} />
                      </div>
                    </div>

                    {(kbForm.type === "template" || kbForm.type === "guideline") && (
                      <div style={{ marginBottom: 10 }}>
                        <label style={lbl}>{kbForm.type === "template" ? "Nombre de la plantilla" : "Título (opcional)"}</label>
                        <input style={inp} placeholder={kbForm.type === "template" ? "Ej: Por reembolso" : "Título corto…"} value={kbForm.title} onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })} />
                      </div>
                    )}

                    {kbForm.type === "example" ? (
                      <>
                        <div style={{ marginBottom: 10 }}>
                          <label style={lbl}>Mensaje del cliente</label>
                          <textarea style={ta} placeholder="Lo que escribió/preguntó el cliente…" value={kbForm.customer_message} onChange={(e) => setKbForm({ ...kbForm, customer_message: e.target.value })} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <label style={lbl}>Respuesta ideal</label>
                          <textarea style={ta} placeholder="Cómo responder (tono y lógica correctos)…" value={kbForm.ideal_reply} onChange={(e) => setKbForm({ ...kbForm, ideal_reply: e.target.value })} />
                        </div>
                      </>
                    ) : (
                      <div style={{ marginBottom: 10 }}>
                        <label style={lbl}>{kbForm.type === "template" ? "Contenido de la plantilla" : "Pauta / regla"}</label>
                        <textarea style={{ ...ta, minHeight: kbForm.type === "template" ? 140 : 64 }} placeholder={kbForm.type === "template" ? "El texto de la respuesta modelo…" : "Ej: nunca prometer reembolsos; tutear; derivar X a Mario…"} value={kbForm.content} onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })} />
                      </div>
                    )}

                    <div style={{ marginBottom: 12 }}>
                      <label style={lbl}>Tags (palabras clave, opcional)</label>
                      <input style={inp} placeholder="contraseña, acceso, factura…" value={kbForm.tags} onChange={(e) => setKbForm({ ...kbForm, tags: e.target.value })} />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <button className={`${classes.btn} ${classes.btnPrimary}`} disabled={kbBusy} onClick={submitKbForm}>
                        <Icon name={kbForm.id ? "check" : "plus"} size={13} /> {kbForm.id ? "Guardar cambios" : "Agregar entrada"}
                      </button>
                      <button className={`${classes.btn} ${classes.btnGhost}`} disabled={kbBusy} onClick={() => { setKbForm(emptyKbForm); setKbFormOpen(false); }}>
                        Cancelar
                      </button>
                      <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
                        <input type="checkbox" checked={kbForm.is_active} onChange={(e) => setKbForm({ ...kbForm, is_active: e.target.checked })} />
                        Activa
                      </label>
                    </div>
                  </div>
                );
              })()}

              {/* Filtro por tipo + lista */}
              {(() => {
                const counts = { all: kbItems.length, template: 0, example: 0, guideline: 0 };
                kbItems.forEach((it) => { counts[it.type] = (counts[it.type] || 0) + 1; });
                const tabs = [
                  { k: "all", l: "Todas" }, { k: "template", l: "Plantillas" },
                  { k: "example", l: "Ejemplos" }, { k: "guideline", l: "Pautas" },
                ];
                const shown = kbFilter === "all" ? kbItems : kbItems.filter((it) => it.type === kbFilter);
                return (
                  <>
                    <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                      {tabs.map((t) => (
                        <button key={t.k} onClick={() => setKbFilter(t.k)}
                          style={{
                            fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 999, cursor: "pointer", border: "1px solid",
                            borderColor: kbFilter === t.k ? "#4F46E5" : "#E2E8F0",
                            background: kbFilter === t.k ? "#EEF2FF" : "#fff",
                            color: kbFilter === t.k ? "#4338CA" : "#475569",
                          }}>
                          {t.l} <span style={{ opacity: 0.65, marginLeft: 2 }}>{counts[t.k] || 0}</span>
                        </button>
                      ))}
                    </div>
                    {shown.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#94A3B8", padding: 24, fontSize: 13 }}>
                        {kbItems.length === 0
                          ? "Todavía no hay entradas. Creá una plantilla arriba o guardá un ejemplo desde una conversación."
                          : "No hay entradas de este tipo."}
                      </div>
                    ) : (
                      shown.map((e) => {
                        const isEx = e.type === "example";
                        return (
                          <div key={e.id} style={{
                            border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginBottom: 8,
                            background: e.is_active ? "#fff" : "#F8FAFC", opacity: e.is_active ? 1 : 0.7,
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                              {(() => {
                                const tb = e.type === "template" ? { l: "PLANTILLA", bg: "#DBEAFE", fg: "#1E40AF" }
                                  : e.type === "guideline" ? { l: "PAUTA", bg: "#FEF3C7", fg: "#92400E" }
                                  : { l: "EJEMPLO", bg: "#EDE9FE", fg: "#5B21B6" };
                                return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: tb.bg, color: tb.fg }}>{tb.l}</span>;
                              })()}
                              {e.mailbox && MAILBOX[e.mailbox] && (
                                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: MAILBOX[e.mailbox].bg, color: MAILBOX[e.mailbox].fg }}>{MAILBOX[e.mailbox].label}</span>
                              )}
                              {e.channel && CHANNEL[e.channel] && (
                                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: "#F1F5F9", color: "#475569" }}>{CHANNEL[e.channel].label}</span>
                              )}
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</span>
                              <button onClick={() => toggleKb(e.id)} title={e.is_active ? "Desactivar" : "Activar"}
                                style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999, cursor: "pointer", border: "1px solid", borderColor: e.is_active ? "#A7F3D0" : "#CBD5E1", background: e.is_active ? "#ECFDF5" : "#fff", color: e.is_active ? "#047857" : "#64748B" }}>
                                {e.is_active ? "Activa" : "Inactiva"}
                              </button>
                              <button className={classes.iconBtn} onClick={() => editKb(e)} title="Editar"><Icon name="edit" size={15} /></button>
                              <button className={classes.iconBtn} onClick={() => deleteKb(e.id)} title="Borrar" style={{ color: "#B91C1C" }}><Icon name="trash" size={15} /></button>
                            </div>
                            <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
                              {isEx ? (
                                <>
                                  <div style={{ marginBottom: 2 }}><b style={{ color: "#334155" }}>Cliente:</b> {e.customer_message || "—"}</div>
                                  <div><b style={{ color: "#334155" }}>Respuesta:</b> {e.ideal_reply || "—"}</div>
                                </>
                              ) : (
                                <div style={{ whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto" }}>{e.content}</div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* ── Marcar como spam ─────────────────────────────────
          Deja elegir si se bloquea el remitente exacto o todo el dominio, porque
          el spam en serie suele rotar la casilla dentro del mismo dominio. */}
      {spamDlg && (() => {
        const m = spamDlg.message;
        const sender = senderOf(m);
        const dom = domainOf(m);
        const target = spamDlg.scope === "domain" ? dom : sender;
        const opt = (scope, title, sub, disabled) => (
          <button
            key={scope}
            disabled={disabled}
            onClick={() => setSpamDlg({ ...spamDlg, scope })}
            style={{
              display: "block", width: "100%", textAlign: "left", cursor: disabled ? "not-allowed" : "pointer",
              padding: "10px 12px", borderRadius: 8, marginBottom: 8, background: "#fff",
              border: spamDlg.scope === scope ? "1.5px solid #DC2626" : "1px solid #E2E8F0",
              opacity: disabled ? 0.45 : 1,
            }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{title}</div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{sub}</div>
          </button>
        );
        return (
          <div
            onClick={() => setSpamDlg(null)}
            style={{
              position: "fixed", inset: 0, zIndex: 10001, background: "rgba(15, 23, 42, 0.55)",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(2px)",
            }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 12, maxWidth: 460, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
              <div style={{ padding: "18px 22px 6px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#FEE2E2", color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="shield" size={18} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Marcar como spam</div>
              </div>
              <div style={{ padding: "4px 22px 6px" }}>
                {opt("address", sender, "Sólo este remitente.", false)}
                {opt("domain", dom ? `Todo @${dom}` : "Todo el dominio",
                  dom ? "Cualquier casilla de ese dominio." : "No aplica: el remitente no es una dirección de correo.",
                  !dom)}
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginTop: 10, marginBottom: 4 }}>
                  Motivo (opcional)
                </label>
                <input
                  value={spamDlg.reason}
                  onChange={(e) => setSpamDlg({ ...spamDlg, reason: e.target.value })}
                  placeholder="Para acordarte por qué lo bloqueaste…"
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none", background: "#F8FAFC" }} />
                <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5, marginTop: 12, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px" }}>
                  Lo que ya haya entrado de {target || "este remitente"} y siga sin responder pasa a Spam, y lo que llegue de ahora en más también.
                  No se le manda ninguna autorespuesta ni se gasta IA en clasificarlo.
                  <br />
                  <b>No toca la casilla de correo:</b> el mail sigue llegando igual a Gmail/webmail, sólo cambia dónde lo ves acá.
                  Se revierte cuando quieras desde Bandeja → Spam → el engranaje.
                </div>
              </div>
              <div style={{ padding: "12px 18px", background: "#F8FAFC", borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button onClick={() => setSpamDlg(null)}
                  style={{ padding: "8px 14px", border: "1px solid #CBD5E1", background: "#fff", color: "#0F172A", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
                  Cancelar
                </button>
                <button disabled={busy || !target} onClick={() => doMarkSpam(m, spamDlg.scope, spamDlg.reason)}
                  style={{ padding: "8px 14px", border: "none", background: "#DC2626", color: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: busy || !target ? 0.6 : 1 }}>
                  Marcar como spam
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Configuración de spam: qué remitentes están bloqueados y cómo revertirlos ── */}
      {spamOpen && (
        <div
          onClick={() => setSpamOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9997, background: "rgba(15, 23, 42, 0.55)",
            display: "flex", justifyContent: "flex-end", backdropFilter: "blur(2px)",
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", width: 640, maxWidth: "100%", height: "100vh", display: "flex", flexDirection: "column", boxShadow: "-8px 0 30px rgba(0,0,0,0.25)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: "#FEE2E2", color: "#B91C1C", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="shield" size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>Remitentes bloqueados</div>
                <div style={{ fontSize: 12, color: "#64748B" }}>
                  {spamRules.length} {spamRules.length === 1 ? "regla" : "reglas"} · sólo afecta a este panel, no a la casilla de correo.
                </div>

              </div>
              <button className={classes.iconBtn} onClick={() => setSpamOpen(false)} title="Cerrar"><Icon name="x" size={18} /></button>
            </div>

            <div style={{ padding: "10px 20px", borderBottom: "1px solid #F1F5F9", flexShrink: 0 }}>
              <input
                value={spamSearch}
                onChange={(e) => setSpamSearch(e.target.value)}
                placeholder="Buscar por dirección, nombre o motivo…"
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none", background: "#F8FAFC" }} />
            </div>

            <div style={{ overflowY: "auto", padding: 20, flex: 1 }}>
              {spamBusy && <div style={{ fontSize: 13, color: "#64748B" }}>Cargando…</div>}
              {!spamBusy && spamRules.length === 0 && (
                <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6 }}>
                  No hay ningún remitente bloqueado.
                  <br />
                  Se agregan desde un mensaje: abrilo y usá <b>Más acciones → Marcar como spam</b>.
                </div>
              )}
              {!spamBusy && spamRules.length > 0 && shownSpamRules.length === 0 && (
                <div style={{ fontSize: 13, color: "#64748B" }}>Ninguna regla coincide con "{spamSearch}".</div>
              )}
              {!spamBusy && shownSpamRules.map((r) => (
                <div key={r.id}
                  style={{
                    border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginBottom: 10,
                    background: r.is_active ? "#fff" : "#F8FAFC", opacity: r.is_active ? 1 : 0.75,
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase",
                      padding: "2px 7px", borderRadius: 999,
                      background: r.match_type === "domain" ? "#FEF3C7" : "#E0E7FF",
                      color: r.match_type === "domain" ? "#92400E" : "#3730A3",
                    }}>
                      {r.match_type === "domain" ? "Dominio" : "Remitente"}
                    </span>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A", wordBreak: "break-all" }}>
                      {r.match_type === "domain" ? `@${r.value}` : r.value}
                    </span>
                    {!r.is_active && (
                      <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999, background: "#E5E7EB", color: "#4B5563" }}>PAUSADA</span>
                    )}
                  </div>
                  {(r.label || r.reason) && (
                    <div style={{ fontSize: 12.5, color: "#475569", marginTop: 4 }}>
                      {r.label && <span>{r.label}</span>}
                      {r.label && r.reason && <span> · </span>}
                      {r.reason && <i>{r.reason}</i>}
                    </div>
                  )}
                  <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 6 }}>
                    {r.messages_in_spam || 0} en Spam
                    {r.hits > 0 && <> · atajó {r.hits} nuevo{r.hits === 1 ? "" : "s"}</>}
                    {r.last_hit_at && <> · último {fmtAgo(new Date(r.last_hit_at))}</>}
                    {r.created_at && <> · creada {fmtAgo(new Date(r.created_at))}</>}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button className={`${classes.btn} ${classes.btnGhost}`} onClick={() => toggleSpamRule(r)}
                      title={r.is_active ? "Deja de atajar mensajes nuevos, pero no devuelve los que ya están en Spam" : "Vuelve a atajar mensajes nuevos"}>
                      <Icon name={r.is_active ? "eye_off" : "shield"} size={13} /> {r.is_active ? "Pausar" : "Reactivar"}
                    </button>
                    <button className={`${classes.btn} ${classes.btnGhost}`} onClick={() => deleteSpamRule(r)}
                      title="Desbloquear y devolver sus mensajes a Pendientes">
                      <Icon name="undo" size={13} /> Desbloquear y restaurar
                    </button>
                    {r.source_message_id && (
                      <button className={`${classes.btn} ${classes.btnGhost}`}
                        onClick={() => { setSpamOpen(false); setSelectedId(r.source_message_id); }}
                        title="Ver el mensaje desde el que se bloqueó">
                        <Icon name="external" size={13} /> Ver mensaje
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {confirmDlg && (
        <div
          onClick={() => confirmDlg.onResolve(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 10001,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, backdropFilter: "blur(2px)",
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, maxWidth: 420, width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden",
            }}>
            <div style={{
              padding: "18px 22px 6px",
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: confirmDlg.danger ? "#FEE2E2" : "#EEF2FF",
                color: confirmDlg.danger ? "#DC2626" : "#4338CA",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name={confirmDlg.danger ? "alert" : "info"} size={18} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>
                {confirmDlg.title}
              </div>
            </div>
            <div style={{ padding: "4px 22px 18px", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
              {confirmDlg.text}
            </div>
            <div style={{
              padding: "12px 18px", background: "#F8FAFC", borderTop: "1px solid #E2E8F0",
              display: "flex", justifyContent: "flex-end", gap: 8,
            }}>
              <button
                onClick={() => confirmDlg.onResolve(false)}
                style={{
                  padding: "8px 14px", border: "1px solid #CBD5E1",
                  background: "#fff", color: "#0F172A", borderRadius: 6,
                  cursor: "pointer", fontSize: 13, fontWeight: 500,
                }}>
                Cancelar
              </button>
              <button
                onClick={() => confirmDlg.onResolve(true)}
                autoFocus
                style={{
                  padding: "8px 14px", border: "none",
                  background: confirmDlg.danger ? "#DC2626" : "#4338CA",
                  color: "#fff", borderRadius: 6,
                  cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>
                {confirmDlg.confirmLabel || "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div
          onClick={() => setToast(null)}
          style={{
            position: "fixed", bottom: 20, right: 20, zIndex: 9999,
            background: toast.kind === "ok" ? "#065F46" : "#991B1B",
            color: "#fff", padding: "12px 16px", borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            fontSize: 13, fontWeight: 500, maxWidth: 360, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 10,
          }}>
          <Icon name={toast.kind === "ok" ? "check" : "alert"} size={16} />
          <span>{toast.text}</span>
        </div>
      )}

      {/* Popup: todos los pagos del alumno */}
      {detailsModal && (
        <div onClick={() => setDetailsModal(null)} style={_ovl}>
          <div onClick={(e) => e.stopPropagation()} style={{ ..._box, width: "92vw", maxWidth: 1180 }}>
            <div style={_mhead}>
              <span style={_mtitle}>Pagos del alumno{detailsModal.student ? ` · ${detailsModal.student.name || detailsModal.student.mail}` : ""}</span>
              <button onClick={() => setDetailsModal(null)} style={_mclose}>✕</button>
            </div>
            {detailsModal.loading ? (
              <div style={{ padding: 24, color: "#64748B", fontSize: 14 }}>Cargando pagos…</div>
            ) : detailsModal.error ? (
              <div style={{ padding: 24, color: "#B91C1C", fontSize: 14 }}>{detailsModal.error}</div>
            ) : (
              <div style={{ padding: 16, overflowY: "auto", flex: 1, minHeight: 0 }}>
                {detailsModal.student && (
                  <div style={{ marginBottom: 12, fontSize: 14, color: "#1F2937" }}>
                    {detailsModal.student.mail} · <span style={{ fontWeight: 800, color: detailsModal.student.active ? "#166534" : "#991B1B" }}>{detailsModal.student.active ? "VIGENTE" : "VENCIDA"}</span>{detailsModal.student.current_end ? ` · vence ${detailsModal.student.current_end}` : ""}
                  </div>
                )}
                {(detailsModal.payments || []).length === 0 ? (
                  <div style={{ color: "#64748B", fontSize: 14 }}>Sin pagos registrados.</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: "#475569", borderBottom: "2px solid #E2E8F0" }}>
                        <th style={_th}>#</th><th style={_th}>Pagado</th><th style={_th}>Plan</th><th style={_th}>País</th><th style={_th}>Monto</th><th style={_th}>Canal</th><th style={_th}>Identificación</th><th style={_th}>Estado</th><th style={_th}>Vigencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsModal.payments.map((p) => (
                        <tr key={p.id} style={{ borderBottom: "1px solid #F1F5F9", color: "#1F2937" }}>
                          <td style={_td}>{p.id}</td>
                          <td style={{ ..._td, whiteSpace: "nowrap" }}>{p.paid_at || "—"}</td>
                          <td style={_td}>{p.plan || "—"}</td>
                          <td style={_td}>{p.country || "—"}</td>
                          <td style={_td}>{p.coin} {p.price}{p.price_usd != null ? ` (USD ${p.price_usd})` : ""}</td>
                          <td style={_td}>{p.pay_channel || "—"}</td>
                          <td style={{ ..._td, whiteSpace: "normal", color: "#475569", maxWidth: 260 }}>
                            {[
                              p.card_last4 ? `•••• ${p.card_last4}` : null,
                              p.mail || null,
                              p.pay_num ? `op ${p.pay_num}` : null,
                              p.validation_code ? `cód ${p.validation_code}` : null,
                            ].filter(Boolean).join(" · ") || "—"}
                          </td>
                          <td style={_td}><span style={{ fontWeight: 700, color: (p.status === "assigned" && !p.cancel) ? "#166534" : "#92400E" }}>{p.cancel ? "cancelado" : p.status}</span></td>
                          <td style={_td}>{p.start || "—"} → {p.end || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {detailsModal.student && (
                  <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #E2E8F0" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: "#4338CA", marginBottom: 4 }}>Asociar un pago confirmado y renovar</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>Buscá el pago que hizo el alumno y asocialo a esta cuenta para extender su membresía. Por defecto: confirmados del país del contacto.</div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                      <input value={assoc.q} onChange={(e) => { const v = e.target.value; setAssoc((s) => ({ ...s, q: v })); }}
                        placeholder="Correo, nombre, últimos 4, nº de operación…"
                        style={{ flex: 1, minWidth: 0, padding: "9px 12px", fontSize: 13.5, border: "1px solid #CBD5E1", borderRadius: 8, outline: "none", color: "#1F2937", boxSizing: "border-box" }} />
                      <select value={assoc.status} onChange={(e) => { const v = e.target.value; setAssoc((s) => ({ ...s, status: v })); }}
                        style={{ flexShrink: 0, padding: "9px 8px", fontSize: 12.5, border: "1px solid #CBD5E1", borderRadius: 8, color: "#1F2937", background: "#fff" }}>
                        <option value="confirmed">Confirmados</option>
                        <option value="assigned">Asignados</option>
                        <option value="all">Todos</option>
                      </select>
                      <select value={assoc.country} onChange={(e) => { const v = e.target.value; setAssoc((s) => ({ ...s, country: v })); }}
                        style={{ flexShrink: 0, maxWidth: 150, padding: "9px 8px", fontSize: 12.5, border: "1px solid #CBD5E1", borderRadius: 8, color: "#1F2937", background: "#fff" }}>
                        <option value="all">Todos los países</option>
                        {countries.map((c) => <option key={c.value} value={c.value}>{c.display}</option>)}
                      </select>
                    </div>
                    {assoc.busy ? (
                      <div style={{ fontSize: 12.5, color: "#64748B" }}>Buscando…</div>
                    ) : (assoc.q.trim().length < 2 && assoc.country === "all") ? (
                      <div style={{ fontSize: 12.5, color: "#94A3B8" }}>Escribí al menos 2 caracteres o elegí un país para ver los pagos.</div>
                    ) : assoc.results.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: "#64748B" }}>Sin pagos que coincidan con esos filtros.</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
                        {assoc.results.map((p) => {
                          const pn = (!p.student && p.payer_info) ? p.payer_info.name : "";
                          const sub = [p.mail, p.country && p.country.name, p.cardLast4 ? `•••• ${p.cardLast4}` : null, p.pay_num ? `op ${p.pay_num}` : null, p.created_at ? new Date(p.created_at).toLocaleDateString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" }) : null].filter(Boolean).join(" · ");
                          return (
                            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #C7D2FE", borderRadius: 8, padding: "7px 10px" }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, color: "#0F172A", fontWeight: 700 }}>{pn || p.mail || `Pago #${p.id}`}{p.price != null ? ` · ${p.price}${p.price_usd != null ? ` (USD ${p.price_usd})` : ""}` : ""}</div>
                                <div style={{ fontSize: 11.5, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
                              </div>
                              <button disabled={assoc.actingId === p.id} onClick={() => assocRenew(p)}
                                style={{ background: "#4338CA", color: "#fff", border: 0, borderRadius: 6, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                                {assoc.actingId === p.id ? "Renovando…" : "Renovar con este pago"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Popup: traza del procesamiento IA (modelo + contexto usado) — para fine-tuning */}
      {trace && (() => {
        const d = trace.data || {};
        const cl = d.classification || {};
        const mdl = d.models || {};
        const pct = (v) => (v == null ? "—" : `${Math.round(Number(v) * 100)}%`);
        const _sec = { marginBottom: 14 };
        const _lbl = { fontSize: 11, fontWeight: 800, color: "#6D28D9", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 };
        const _pre = { margin: 0, padding: "10px 12px", background: "#0F172A", color: "#E2E8F0", borderRadius: 8, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflowY: "auto", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };
        const _chip = { fontSize: 12, fontWeight: 700, color: "#3730A3", background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 999, padding: "3px 10px" };
        return (
          <div onClick={() => setTrace(null)} style={_ovl}>
            <div onClick={(e) => e.stopPropagation()} style={{ ..._box, width: "92vw", maxWidth: 860 }}>
              <div style={_mhead}>
                <span style={_mtitle}>🛠️ Cómo se generó esta respuesta</span>
                <button onClick={() => setTrace(null)} style={_mclose}>✕</button>
              </div>
              {trace.loading ? (
                <div style={{ padding: 24, color: "#64748B", fontSize: 14 }}>Cargando traza…</div>
              ) : trace.error ? (
                <div style={{ padding: 24, color: "#B91C1C", fontSize: 14 }}>{trace.error}</div>
              ) : (
                <div style={{ padding: 16, overflowY: "auto", flex: 1, minHeight: 0 }}>
                  <div style={_sec}>
                    <div style={_lbl}>Modelos (pipeline en 2 pasos)</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span style={_chip}>1 · Moderación / señales: <b>{mdl.moderacion || "—"}</b></span>
                      <span style={_chip}>2 · Redacción de la respuesta: <b>{mdl.redaccion || "—"}</b></span>
                    </div>
                    {cl.classified_at && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 5 }}>Clasificado: {cl.classified_at}</div>}
                  </div>

                  <div style={_sec}>
                    <div style={_lbl}>Señales de clasificación</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "#334155" }}>
                      <span style={_chip}>sentimiento: {cl.sentiment || "—"} ({pct(cl.sentiment_score)})</span>
                      <span style={_chip}>intención de compra: {pct(cl.sales_intent)}</span>
                      <span style={_chip}>hostilidad: {pct(cl.hostility)}{cl.hostility_target && cl.hostility_target !== "none" ? ` → ${cl.hostility_target}` : ""}</span>
                      <span style={_chip}>pidió activación: {cl.activation_request ? "sí" : "no"}</span>
                    </div>
                    {cl.reasoning && <div style={{ marginTop: 6, fontSize: 12.5, color: "#475569", fontStyle: "italic" }}>“{cl.reasoning}”</div>}
                    {cl.payment_hints && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}>
                        Datos de pago detectados: {[
                          cl.payment_hints.country ? `país ${cl.payment_hints.country}` : null,
                          cl.payment_hints.card_last4 ? `•••• ${cl.payment_hints.card_last4}` : null,
                          cl.payment_hints.payer_email || null,
                          cl.payment_hints.activation_code ? `cód/op ${cl.payment_hints.activation_code}` : null,
                        ].filter(Boolean).join(" · ") || "—"}
                      </div>
                    )}
                  </div>

                  {Array.isArray(d.receipts) && d.receipts.length > 0 && (
                    <div style={_sec}>
                      <div style={_lbl}>Comprobantes analizados</div>
                      {d.receipts.map((r, i) => (
                        <div key={i} style={{ fontSize: 12.5, color: "#334155", marginBottom: 3 }}>📎 {r.summary || [r.method, r.currency, r.amount].filter(Boolean).join(" ")}</div>
                      ))}
                    </div>
                  )}

                  {d.thread_context && (
                    <div style={_sec}>
                      <div style={_lbl}>Hilo previo que leyó la IA</div>
                      <pre style={_pre}>{d.thread_context}</pre>
                    </div>
                  )}

                  <div style={_sec}>
                    <div style={_lbl}>Contexto / base de conocimiento inyectada {d.knowledge_chars ? `(${d.knowledge_chars.toLocaleString()} caracteres)` : ""}</div>
                    <div style={{ fontSize: 11.5, color: "#94A3B8", marginBottom: 5 }}>Pautas, plantillas, ejemplos reales de Mario, catálogo de cursos, precios/país y estado del cliente que el modelo recibió para responder. Editá esto en <b>Base de conocimiento</b>.</div>
                    <pre style={_pre}>{d.knowledge || "(sin contexto adicional)"}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Popup: buscar alumno con filtros */}
      {searchOpen && (
        <div onClick={() => setSearchOpen(false)} style={_ovl}>
          <div onClick={(e) => e.stopPropagation()} style={{ ..._box, width: 660 }}>
            <div style={_mhead}>
              <span style={_mtitle}>🔎 Buscar alumno</span>
              <button onClick={() => setSearchOpen(false)} style={_mclose}>✕</button>
            </div>
            <div style={{ padding: 16, overflowY: "auto" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runStudentSearch(); }}
                  placeholder="Nombre, email o últimos 4 de la tarjeta…"
                  style={{ flex: 1, padding: "9px 12px", fontSize: 14, border: "1px solid #CBD5E1", borderRadius: 8, outline: "none", color: "#1F2937" }} />
                <select value={searchActive} onChange={(e) => setSearchActive(e.target.value)}
                  style={{ padding: "9px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 8, color: "#1F2937" }}>
                  <option value="all">Todos</option>
                  <option value="active">Vigentes</option>
                  <option value="inactive">Vencidos</option>
                </select>
                <button onClick={runStudentSearch} disabled={searchBusy}
                  style={{ background: "#4338CA", color: "#fff", border: 0, borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>{searchBusy ? "…" : "Buscar"}</button>
              </div>
              <div style={{ maxHeight: 380, overflowY: "auto" }}>
                {searchResults.length === 0 ? (
                  <div style={{ color: "#64748B", fontSize: 13.5, padding: "8px 2px" }}>
                    {searchBusy ? "Buscando…" : searchTouched ? "Sin resultados. Probá con otro dato (mín. 2 caracteres)." : "Escribí un nombre, email o últimos 4 de tarjeta y tocá Buscar."}
                  </div>
                ) : searchResults.map((s) => (
                  <div key={s.id} onClick={() => pickStudent(s)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderBottom: "1px solid #F1F5F9", cursor: "pointer" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#F8FAFC"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{s.name || s.mail}</div>
                      <div style={{ fontSize: 12.5, color: "#475569" }}>{s.mail}{s.current_end ? ` · vence ${s.current_end}` : ""}</div>
                    </div>
                    <span style={{ fontSize: 11.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: s.active ? "#DCFCE7" : "#FEE2E2", color: s.active ? "#166534" : "#991B1B" }}>{s.active ? "VIGENTE" : "VENCIDA"}</span>
                    <button style={{ background: "#4338CA", color: "#fff", border: 0, borderRadius: 6, padding: "5px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Elegir</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Popup: buscador rápido de pagos (mismo backend que /admin/payments) */}
      {payOpen && (
        <div onClick={() => { setPayOpen(false); setManualNew(null); setActivatePay(null); }} style={_ovl}>
          <div onClick={(e) => e.stopPropagation()} style={{ ..._box, width: 860 }}>
            <div style={_mhead}>
              <span style={_mtitle}>💳 Buscar pagos</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 12, alignItems: "center" }}>
                <a href="#/admin/payments" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: "#4338CA", textDecoration: "none" }}>Abrir Pagos completo →</a>
                <button onClick={() => { setPayOpen(false); setManualNew(null); setActivatePay(null); }} style={_mclose}>✕</button>
              </span>
            </div>
            <div style={{ padding: 16, overflowY: "auto" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input autoFocus value={paySearch} onChange={(e) => setPaySearch(e.target.value)}
                  placeholder="Mail, nombre, apellido, últimos 4 de la tarjeta, nº de operación…"
                  style={{ flex: 1, minWidth: 0, padding: "10px 12px", fontSize: 14, border: "1px solid #CBD5E1", borderRadius: 8, outline: "none", color: "#1F2937", boxSizing: "border-box" }} />
                <select value={payStatus} onChange={(e) => { const v = e.target.value; setPayStatus(v); }}
                  style={{ flexShrink: 0, padding: "10px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 8, color: "#1F2937", background: "#fff" }}>
                  <option value="all">Todos</option>
                  <option value="confirmed">Confirmados</option>
                  <option value="assigned">Asignados</option>
                </select>
                <select value={payCountry} onChange={(e) => { const v = e.target.value; setPayCountry(v); }}
                  style={{ flexShrink: 0, maxWidth: 160, padding: "10px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 8, color: "#1F2937", background: "#fff" }}>
                  <option value="all">Todos los países</option>
                  {countries.map((c) => <option key={c.value} value={c.value}>{c.display}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 12.5, color: "#64748B", marginBottom: 8 }}>
                {payBusy ? "Buscando…" : payTotal !== null ? `${(payTotal || 0).toLocaleString("es-AR")} ${payTotal === 1 ? "pago" : "pagos"}${paySearch.trim() ? ` · mostrando ${payResults.length}` : " (más recientes)"}` : ""}
              </div>
              {!manualNew ? (
                <button onClick={openManualNew} style={{ marginBottom: 10, background: "#ECFDF5", color: "#047857", border: "1px solid #6EE7B7", borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>➕ Crear alumno sin pago (cuando no aparece acá)</button>
              ) : (
                <div style={{ marginBottom: 12, padding: "12px 14px", background: "#F0FDF4", border: "1px solid #6EE7B7", borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#047857", marginBottom: 8 }}>Crear alumno sin pago</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                    <input value={manualNew.email} disabled={manualNew.busy} onChange={(e) => { const v = e.target.value; setManualNew((s) => s ? { ...s, email: v } : s); }} placeholder="Correo del alumno"
                      style={{ flex: "2 1 240px", padding: "8px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, outline: "none", color: "#1F2937" }} />
                    <input value={manualNew.name} disabled={manualNew.busy} onChange={(e) => { const v = e.target.value; setManualNew((s) => s ? { ...s, name: v } : s); }} placeholder="Nombre"
                      style={{ flex: "1 1 120px", padding: "8px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, outline: "none", color: "#1F2937" }} />
                    <input value={manualNew.lastname} disabled={manualNew.busy} onChange={(e) => { const v = e.target.value; setManualNew((s) => s ? { ...s, lastname: v } : s); }} placeholder="Apellido"
                      style={{ flex: "1 1 120px", padding: "8px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, outline: "none", color: "#1F2937" }} />
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                    <select value={manualNew.country_id} disabled={manualNew.busy} onChange={(e) => { const v = e.target.value; setManualNew((s) => s ? { ...s, country_id: v } : s); }}
                      style={{ flex: "1 1 180px", padding: "8px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, color: "#1F2937", background: "#fff" }}>
                      <option value="">País (opcional)</option>
                      {countries.map((c) => <option key={c.value} value={c.value}>{c.display}</option>)}
                    </select>
                    <label style={{ fontSize: 12.5, color: "#475569", display: "flex", alignItems: "center", gap: 6 }}>
                      Vigencia
                      <input type="number" min="1" max="50" value={manualNew.years} disabled={manualNew.busy} onChange={(e) => { const v = e.target.value; setManualNew((s) => s ? { ...s, years: v } : s); }}
                        style={{ width: 56, padding: "8px 8px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, color: "#1F2937" }} /> años
                    </label>
                  </div>
                  {manualNew.error && <div style={{ fontSize: 12, color: "#B91C1C", marginBottom: 6 }}>{manualNew.error}</div>}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button disabled={manualNew.busy} onClick={submitManualNew}
                      style={{ background: "#047857", color: "#fff", border: 0, borderRadius: 6, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{manualNew.busy ? "Creando…" : "Crear y abrir mensaje"}</button>
                    <button disabled={manualNew.busy} onClick={() => setManualNew(null)}
                      style={{ background: "#fff", color: "#047857", border: "1px solid #6EE7B7", borderRadius: 6, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
                  </div>
                </div>
              )}
              <div style={{ maxHeight: 440, overflowY: "auto" }}>
                {payResults.length === 0 ? (
                  <div style={{ color: "#64748B", fontSize: 13.5, padding: "8px 2px" }}>{payBusy ? "Buscando…" : "Sin resultados."}</div>
                ) : payResults.map((p) => {
                  const pinf = p.payer_info || {};
                  const name = (p.student ? `${p.student.name || ""} ${p.student.lastname || ""}`.trim() : "") || pinf.name || "";
                  const payerLine = [
                    pinf.name && !p.student ? `pagador: ${pinf.name}` : null,
                    (pinf.doc_number) ? `${pinf.doc_type || "doc"} ${pinf.doc_number}` : null,
                    pinf.phone || null,
                    (pinf.email && pinf.email !== p.mail) ? pinf.email : null,
                  ].filter(Boolean).join(" · ");
                  const assigned = p.status === "assigned" && p.student_id;
                  const card = p.cardLast4 ? `${p.cardBrand ? p.cardBrand + " " : ""}•••• ${p.cardLast4}` : "";
                  const _dt = p.created_at ? new Date(p.created_at) : null;
                  const dDate = _dt ? _dt.toLocaleDateString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", day: "2-digit", month: "2-digit", year: "2-digit" }) : "";
                  const dTime = _dt ? _dt.toLocaleTimeString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", hour: "2-digit", minute: "2-digit", hour12: false }) : "";
                  const meta = p.status === "assigned" ? { t: "Asignado", bg: "#DCFCE7", c: "#166534" } : p.status === "confirmed" ? { t: "Confirmado", bg: "#DBEAFE", c: "#1E40AF" } : { t: p.status || "—", bg: "#F1F5F9", c: "#475569" };
                  return (
                    <div key={p.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", borderBottom: "1px solid #F1F5F9" }}>
                      <div title={_dt ? _dt.toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" }) : ""} style={{ width: 62, flexShrink: 0, lineHeight: 1.2, paddingTop: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", whiteSpace: "nowrap" }}>{dDate || "—"}</div>
                        {dTime && <div style={{ fontSize: 10.5, color: "#94A3B8" }}>{dTime}</div>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <b style={{ fontSize: 13.5, color: "#0F172A" }}>{name || p.mail || "(sin nombre)"}</b>
                        <span style={{ fontSize: 11, fontWeight: 800, padding: "1px 8px", borderRadius: 999, background: meta.bg, color: meta.c }}>{meta.t}</span>
                        {p.pay_num && String(p.pay_num).startsWith("manual-")
                          ? <span title="Alta manual sin cobro (membresía otorgada a mano)" style={{ fontSize: 11, fontWeight: 800, color: "#92400E", background: "#FEF3C7", padding: "1px 7px", borderRadius: 999 }}>alta manual</span>
                          : (p.price != null && <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 600 }}>{p.price}{p.price_usd != null ? ` · USD ${p.price_usd}` : ""}</span>)}
                        {assigned && <a href={`#/admin/Student-new/${p.student_id}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 700, color: "#4338CA", textDecoration: "none" }}>ver ficha →</a>}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                        {(p.student && p.student.mail) ? p.student.mail : (p.mail || "")}
                        {card ? ` · ${card}` : ""}
                        {p.pay_channel && p.pay_channel.name ? ` · ${p.pay_channel.name}` : ""}
                        {p.pay_num ? ` · op ${p.pay_num}` : ""}
                      </div>
                      {payerLine && (
                        <div style={{ fontSize: 11.5, color: "#7C3AED", marginTop: 2 }}>👤 {payerLine}</div>
                      )}
                      {p.status === "confirmed" && !p.student_id && (
                        (activatePay && activatePay.p.id === p.id) ? (
                          <div style={{ marginTop: 8, padding: "10px 12px", background: "#F5F3FF", border: "1px solid #C7B6F5", borderRadius: 8 }}>
                            {activatePay.loadingPreview ? (
                              <div style={{ fontSize: 12.5, color: "#6D28D9" }}>Calculando membresía…</div>
                            ) : (
                              <>
                                <div style={{ fontSize: 12.5, color: "#4338CA", fontWeight: 700, marginBottom: 6 }}>
                                  {activatePay.preview ? (
                                    <>País: {activatePay.preview.country || "—"} · Membresía: {activatePay.preview.duration_years ? `${activatePay.preview.duration_years} año${activatePay.preview.duration_years === 1 ? "" : "s"}` : "—"}{activatePay.preview.projected_end ? ` → vence ${activatePay.preview.projected_end}` : ""}</>
                                  ) : "No se pudo calcular la membresía."}
                                </div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                                  <input value={activatePay.email} disabled={activatePay.busy} onChange={(e) => { const v = e.target.value; setActivatePay((s) => (s ? { ...s, email: v } : s)); }} placeholder="Correo del alumno"
                                    style={{ flex: "2 1 220px", padding: "7px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, outline: "none", color: "#1F2937" }} />
                                  <input value={activatePay.name} disabled={activatePay.busy} onChange={(e) => { const v = e.target.value; setActivatePay((s) => (s ? { ...s, name: v } : s)); }} placeholder="Nombre"
                                    style={{ flex: "1 1 120px", padding: "7px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, outline: "none", color: "#1F2937" }} />
                                  <input value={activatePay.lastname} disabled={activatePay.busy} onChange={(e) => { const v = e.target.value; setActivatePay((s) => (s ? { ...s, lastname: v } : s)); }} placeholder="Apellido"
                                    style={{ flex: "1 1 120px", padding: "7px 10px", fontSize: 13, border: "1px solid #CBD5E1", borderRadius: 6, outline: "none", color: "#1F2937" }} />
                                </div>
                                {activatePay.preview && activatePay.preview.has_account && (
                                  <div style={{ fontSize: 11.5, color: "#92400E", marginBottom: 6 }}>Ya existe una cuenta con ese correo — se usará esa cuenta (no se duplica).</div>
                                )}
                                {activatePay.error && <div style={{ fontSize: 12, color: "#B91C1C", marginBottom: 6 }}>{activatePay.error}</div>}
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button disabled={activatePay.busy} onClick={submitActivatePay}
                                    style={{ background: "#4338CA", color: "#fff", border: 0, borderRadius: 6, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{activatePay.busy ? "Creando…" : "Crear y abrir mensaje"}</button>
                                  <button disabled={activatePay.busy} onClick={() => setActivatePay(null)}
                                    style={{ background: "#fff", color: "#4338CA", border: "1px solid #A5B4FC", borderRadius: 6, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <button onClick={() => openActivatePay(p)}
                            style={{ marginTop: 6, background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE", borderRadius: 6, padding: "5px 11px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>➕ Crear alumno + activar</button>
                        )
                      )}
                      </div>
                      {p.country && p.country.name && (
                        <div style={{ flexShrink: 0, paddingTop: 1 }}>
                          <span title="País del pago" style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#CFFAFE", color: "#155E75", border: "1px solid #67E8F9", fontSize: 11.5, fontWeight: 800, padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>
                            {countryFlag(p.country.name) ? <span style={{ fontSize: 12, lineHeight: 1 }}>{countryFlag(p.country.name)}</span> : <Icon name="globe" size={11} />} {p.country.name}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agente de atención: flota por encima de todo el Centro de Mensajes.
          Va último para que quede sobre los paneles sin pelear z-index. */}
      <AgentBubble />
    </div>
  );
}
